import { useRef, useEffect, useCallback, useState } from 'react';
import soundManager from '../utils/SoundManager';

// Import game modules from refactored files
import {
  GRAVITY,
  JUMP_FORCE,
  MOVE_SPEED,
  BULLET_SPEED,
  ENEMY_SPEED,
  GROUND_HEIGHT,
  POWER_UP_TYPES,
  WEAPON_TYPES,
  loadBackgroundImage,
  DEPTH_COLLISION_THRESHOLD,
  BULLET_DEPTH_THRESHOLD,
  DEFAULT_GROUND_Y,
  WALKABLE_DEPTH,
  ROAD_TOP_MARGIN,
  getScreenFeetY,
  getDrawY,
  getDepthScale
} from '../game/constants';
import { Player } from '../game/Player';
import { Enemy, getBossTypeForLevel } from '../game/Enemy';
import { Bullet, PowerUp, Particle, BrassCasing, FloatingText } from '../game/entities';
import { drawBackground } from '../game/background';
import { drawEnvironmentParticles as drawEnvironmentParticlesFn, drawMuzzleFlash as drawMuzzleFlashFn } from '../game/gameEffects';
import { reportTransitionWatchdog } from '../lib/telemetry';

export const useGameEngine = (canvasRef, playerColor, onGameOver, onScoreUpdate, ownedWeapons = [], playerSpriteUrl = null, nftBonuses = null, nftId = null, multiplayerSync = null, tournamentTimeLimit = 0) => {
  const [gameState, setGameState] = useState({
    score: 0,
    level: 1,
    kills: 0,
    lives: 0,
    isPlaying: false,
    isPaused: false,
    isGameOver: false,
    grenades: 0,
    bossActive: false,
    bossDefeated: false,
    showLevelTransition: false,
    levelTransitionTimer: 0,
    transitionPhase: null,
    defeatedBossName: null,    // P-2 stinger label
    defeatedBossReward: null,  // P-2 stinger reward chip
    tournamentTimeRemaining: tournamentTimeLimit > 0 ? tournamentTimeLimit : null,
    rocketAmmo: 0,
    tankAmmo: 0,
    rifleAmmo: 0,
    currentWeapon: 'default',
    combo: 0, // P-1 live combo count for HUD
    // NFT ability cooldowns and states
    nftAbilities: {
      spawnShieldActive: false,
      spawnShieldRemaining: 0,
      burnAuraActive: false,
      burnAuraCooldown: 0,
      slowAuraActive: false,
      chainLightningReady: false,
      chainLightningCounter: 0,
      healthRegenCooldown: 0,
      lastChainLightning: false
    }
  });

  // Multiplayer state ref
  const multiplayerSyncRef = useRef(multiplayerSync);
  multiplayerSyncRef.current = multiplayerSync;
  const lastSyncTime = useRef(0);
  const SYNC_INTERVAL = 50; // Sync every 50ms

  // Store NFT bonuses in a ref for game loop access
  const nftBonusesRef = useRef(nftBonuses);
  nftBonusesRef.current = nftBonuses;

  const gameDataRef = useRef({
    player: null,
    enemies: [],
    bullets: [],
    particles: [],
    powerUps: [],
    brassCasings: [],
    deathAnimations: [], // Track dying enemies with animations
    floatingTexts: [],   // "+50" combat-text that rises + fades on kills (P-2)
    keys: { left: false, right: false, up: false, down: false, jump: false, shoot: false, grenade: false, switchWeapon: false },
    // Authoritative current level — mirrors gameState.level but updated
    // synchronously on transition so the game loop closure never reads stale.
    // Fixes: bg/ground not changing after boss death (P-1 production bug).
    level: 1,
    cameraX: 0,
    lastShot: 0,
    spawnTimer: 0,
    powerUpTimer: 0,
    levelProgress: 0,
    soundInitialized: false,
    bossSpawned: false,
    levelTransitionStart: 0,
    lastWeaponSwitch: 0,
    gameRunning: false,
    // Kill streak / boss / life-drop tracking
    currentKillStreak: 0,
    maxKillStreak: 0,
    bossesDefeated: 0,
    lastKillCount: 0,
    killsForLifeDrop: 0,
    nextLifeDropAt: 15 + Math.floor(Math.random() * 6), // 15-20
    // Extra Life (♥) authoritative counter — read/written inside the game loop
    // to avoid stale-closure races when a heart pickup and player death land
    // in the same frame. Mirrored into React `gameState.lives` for HUD render.
    lives: 0,
    // Combo meter (P-1): consecutive kills within COMBO_WINDOW_MS — colored
    // floats, scaling, glow aura, and bullet damage bonus
    comboCount: 0,
    lastKillTime: 0,
    comboPeak: 0,
    // Visual effects
    screenShake: { intensity: 0, duration: 0 },
    muzzleFlash: { active: false, x: 0, y: 0, timer: 0 },
    environmentParticles: [],
    // Revival animation (Extra Life trigger): pink flash + heart particles + slow-mo
    revivalEffect: { active: false, timer: 0, duration: 0 },
    // NFT ability timers
    nftAbilityTimers: {
      spawnShieldTimer: 0,
      healthRegenTimer: 0,
      chainLightningCounter: 0,
      burnAuraTick: 0
    },
    // Tournament timer
    tournamentTimer: tournamentTimeLimit > 0 ? tournamentTimeLimit : 0,
    tournamentLastTick: 0
  });

  const animationFrameRef = useRef(null);

  const spawnBoss = useCallback((canvasWidth, level) => {
    const data = gameDataRef.current;
    const x = data.cameraX + canvasWidth + 150;
    const difficulty = Math.min(5, Math.ceil(level / 2));
    // Get level-appropriate boss type
    const bossType = getBossTypeForLevel(level);
    data.enemies.push(new Enemy(x, 0, bossType, difficulty, level));
    data.bossSpawned = true;
    setGameState(prev => ({ ...prev, bossActive: true }));
  }, []);

  const spawnEnemy = useCallback((canvasWidth, level) => {
    const data = gameDataRef.current;
    
    // Don't spawn regular enemies during boss fight
    if (data.bossSpawned) return;
    
    const x = data.cameraX + canvasWidth + 100;
    const difficulty = Math.min(5, Math.ceil(level / 2)); // Difficulty 1-5
    
    // Enemy type probability based on level
    const rand = Math.random();
    let type;
    
    if (level <= 2) {
      // Early levels: Easy enemies + jumpers
      if (rand < 0.35) type = 'grunt';
      else if (rand < 0.6) type = 'soldier';
      else if (rand < 0.8) type = 'runner';
      else type = 'jumper';
    } else if (level <= 4) {
      // Levels 3-4: Mix of easy and medium
      if (rand < 0.2) type = 'grunt';
      else if (rand < 0.4) type = 'soldier';
      else if (rand < 0.55) type = 'runner';
      else if (rand < 0.7) type = 'jumper';
      else if (rand < 0.85) type = 'tank';
      else type = 'drone';
    } else if (level <= 6) {
      // Levels 5-6: Medium and some hard — introduce brute
      if (rand < 0.15) type = 'soldier';
      else if (rand < 0.28) type = 'jumper';
      else if (rand < 0.4) type = 'tank';
      else if (rand < 0.5) type = 'commando';
      else if (rand < 0.6) type = 'ninja';
      else if (rand < 0.7) type = 'brute';
      else if (rand < 0.85) type = 'helicopter';
      else type = 'drone';
    } else if (level <= 8) {
      // Levels 7-8: Hard enemies — introduce sniper
      if (rand < 0.1) type = 'jumper';
      else if (rand < 0.22) type = 'commando';
      else if (rand < 0.35) type = 'ninja';
      else if (rand < 0.48) type = 'mech';
      else if (rand < 0.58) type = 'berserker';
      else if (rand < 0.68) type = 'brute';
      else if (rand < 0.78) type = 'sniper';
      else if (rand < 0.9) type = 'helicopter';
      else type = 'gunship';
    } else if (level <= 12) {
      // Levels 9-12: Very hard enemies — introduce teleporter
      if (rand < 0.12) type = 'commando';
      else if (rand < 0.24) type = 'ninja';
      else if (rand < 0.36) type = 'berserker';
      else if (rand < 0.46) type = 'elite';
      else if (rand < 0.56) type = 'mech';
      else if (rand < 0.66) type = 'brute';
      else if (rand < 0.76) type = 'sniper';
      else if (rand < 0.86) type = 'teleporter';
      else if (rand < 0.94) type = 'gunship';
      else type = 'helicopter';
    } else {
      // Levels 13-20: End-game — all elite tiers with high new-enemy frequency
      if (rand < 0.1) type = 'ninja';
      else if (rand < 0.22) type = 'berserker';
      else if (rand < 0.34) type = 'elite';
      else if (rand < 0.44) type = 'mech';
      else if (rand < 0.56) type = 'brute';
      else if (rand < 0.68) type = 'sniper';
      else if (rand < 0.82) type = 'teleporter';
      else if (rand < 0.92) type = 'gunship';
      else type = 'helicopter';
    }
    
    data.enemies.push(new Enemy(x, 0, type, difficulty, level));
  }, []);

  const spawnPowerUp = useCallback((canvasWidth, forcedType = null) => {
    const data = gameDataRef.current;
    const x = data.cameraX + canvasWidth + 50;
    let type = forcedType;
    if (!type) {
      // Exclude EXTRA_LIFE from the random pool — it should only drop on kill milestones
      const types = Object.values(POWER_UP_TYPES).filter(t => t !== POWER_UP_TYPES.EXTRA_LIFE);
      type = types[Math.floor(Math.random() * types.length)];
    }
    data.powerUps.push(new PowerUp(x, 0, type));
  }, []);

  // Kill streak tracker + extra-life drop trigger (every 15-20 kills)
  useEffect(() => {
    const data = gameDataRef.current;
    const newCount = gameState.kills;
    if (newCount <= data.lastKillCount) return;
    const delta = newCount - data.lastKillCount;
    data.lastKillCount = newCount;

    // Update kill streak (resets on player death — see player-death handler)
    data.currentKillStreak += delta;
    if (data.currentKillStreak > data.maxKillStreak) {
      data.maxKillStreak = data.currentKillStreak;
    }

    // Extra-life drop: spawn one heart power-up every 15-20 kills.
    // In multiplayer, only the HOST authoritatively spawns power-ups; guest
    // streak still tracks for stats, but spawning happens host-side and is
    // mirrored to guest via enemy_sync.
    data.killsForLifeDrop += delta;
    if (data.killsForLifeDrop >= data.nextLifeDropAt) {
      data.killsForLifeDrop = 0;
      data.nextLifeDropAt = 15 + Math.floor(Math.random() * 6); // 15-20
      const mp = multiplayerSyncRef.current;
      const isGuest = mp && !mp.isHost;
      const canvas = canvasRef.current;
      if (canvas && !isGuest) {
        spawnPowerUp(canvas.width, POWER_UP_TYPES.EXTRA_LIFE);
      }
    }
  }, [gameState.kills, canvasRef, spawnPowerUp]);

  const checkCollision = (a, b) => {
    return a.x < b.x + b.width &&
           a.x + a.width > b.x &&
           a.y < b.y + b.height &&
           a.y + a.height > b.y;
  };

  // === COMBO METER (P-1) ===
  // Tunables: 2.5s between kills to keep streak alive. Tier breakpoints govern
  // float color/scale, player aura intensity, and bullet damage multiplier.
  const COMBO_WINDOW_MS = 2500;
  const getComboTier = (count) => {
    if (count >= 10) return { label: 'GODLIKE', color: '#00FFFF', scale: 1.8, damageMult: 2.0, glow: 1.0 };
    if (count >= 7)  return { label: 'INSANE',  color: '#FF1493', scale: 1.55, damageMult: 1.7, glow: 0.85 };
    if (count >= 5)  return { label: 'RAMPAGE', color: '#FF3B30', scale: 1.35, damageMult: 1.5, glow: 0.65 };
    if (count >= 3)  return { label: 'COMBO',   color: '#FF9500', scale: 1.18, damageMult: 1.25, glow: 0.45 };
    return { label: '', color: '#FFD700', scale: 1, damageMult: 1, glow: 0 };
  };

  // === MULTIPLAYER KILL ATTRIBUTION ===
  // Credit a kill to the right player. If the killer-bullet (or fire DoT source)
  // came from a remote peer, broadcast a `credit_kill` event addressed to the
  // original shooter and skip local score; otherwise increment local score+kills.
  // Also spawns a floating "+points" combat-text at the killing player's position
  // (P-2 visible feedback for both local and remote scoring).
  const creditKill = useCallback((points, killerBullet) => {
    const data = gameDataRef.current;
    const player = data.player;
    const mp = multiplayerSyncRef.current;
    if (mp && killerBullet && killerBullet.isRemote && killerBullet.shooter) {
      // Remote shooter scored on our local sim — credit them and float text
      // above their on-screen player position (host watches the +50 over peer)
      const peer = mp.otherPlayers?.[killerBullet.shooter];
      if (peer) {
        data.floatingTexts.push(new FloatingText(
          peer.x + 25, peer.y - 10, `+${points}`, '#9945FF'
        ));
      }
      mp.sendGameAction('credit_kill', {
        shooter: killerBullet.shooter,
        points: points
      });
      return;
    }
    // Local kill — bump combo first, then float colored "+points" sized by tier
    const now = Date.now();
    if (now - data.lastKillTime < COMBO_WINDOW_MS) {
      data.comboCount += 1;
    } else {
      data.comboCount = 1;
    }
    data.lastKillTime = now;
    if (data.comboCount > data.comboPeak) data.comboPeak = data.comboCount;
    const tier = getComboTier(data.comboCount);
    if (player) {
      const float = new FloatingText(
        player.x + player.width / 2, player.y - 10, `+${points}`, tier.color
      );
      // Apply tier scale boost on top of the entity's pop-in scale
      float.scale = tier.scale;
      data.floatingTexts.push(float);
      // Milestone banner: "5x RAMPAGE!" at exactly the breakpoints
      if (data.comboCount === 3 || data.comboCount === 5 || data.comboCount === 7 || data.comboCount === 10) {
        const banner = new FloatingText(
          player.x + player.width / 2,
          player.y - 35,
          `${data.comboCount}x ${tier.label}!`,
          tier.color
        );
        banner.scale = tier.scale * 1.1;
        banner.driftY = -1.0;
        banner.decay = 0.012; // banner lingers a bit longer
        data.floatingTexts.push(banner);
      }
    }
    setGameState(prev => ({
      ...prev,
      score: prev.score + points,
      kills: prev.kills + 1
    }));
    onScoreUpdate?.(points);
  }, [onScoreUpdate]);


  // 2.5D depth-aware collision: checks AABB + depth proximity
  const checkCollision2D = (a, b, depthThreshold = DEPTH_COLLISION_THRESHOLD) => {
    // AABB check on screen coordinates
    const aabb = a.x < b.x + b.width &&
                 a.x + a.width > b.x &&
                 a.y < b.y + b.height &&
                 a.y + a.height > b.y;
    if (!aabb) return false;
    // Flying enemies can be hit at any depth
    if (b.isFlying && b.isFlying()) return true;
    if (a.isFlying && a.isFlying()) return true;
    // Check depth proximity
    const aDepth = a.groundY !== undefined ? a.groundY : DEFAULT_GROUND_Y;
    const bDepth = b.groundY !== undefined ? b.groundY : DEFAULT_GROUND_Y;
    return Math.abs(aDepth - bDepth) < depthThreshold;
  };

  const createExplosion = (x, y, color, count = 10) => {
    const data = gameDataRef.current;
    for (let i = 0; i < count; i++) {
      data.particles.push(new Particle(x, y, color));
    }
    soundManager.playExplosion();
  };

  // Screen shake effect
  const triggerScreenShake = (intensity = 5, duration = 10) => {
    const data = gameDataRef.current;
    data.screenShake.intensity = Math.max(data.screenShake.intensity, intensity);
    data.screenShake.duration = Math.max(data.screenShake.duration, duration);
  };

  // Muzzle flash effect
  const triggerMuzzleFlash = (x, y) => {
    const data = gameDataRef.current;
    data.muzzleFlash = { active: true, x, y, timer: 3 };
  };

  // Create directional sparks (for hits)
  const createSparks = (x, y, direction, color, count = 8) => {
    const data = gameDataRef.current;
    for (let i = 0; i < count; i++) {
      const angle = (direction === 1 ? 0 : Math.PI) + (Math.random() - 0.5) * 0.8;
      const speed = 4 + Math.random() * 6;
      data.particles.push(new Particle(
        x, y, color,
        3 + Math.random() * 3,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed - 2
      ));
    }
  };

  // Create dust particles when running
  const createRunningDust = (x, y) => {
    const data = gameDataRef.current;
    if (Math.random() < 0.3) {
      data.particles.push(new Particle(
        x + Math.random() * 20,
        y,
        '#8B7355',
        2 + Math.random() * 3,
        (Math.random() - 0.5) * 2,
        -Math.random() * 2
      ));
    }
  };

  // Spawn floating environment particles
  const updateEnvironmentParticles = (canvas, cameraX) => {
    const data = gameDataRef.current;
    // Add new particles occasionally
    if (Math.random() < 0.05 && data.environmentParticles.length < 30) {
      data.environmentParticles.push({
        x: cameraX + Math.random() * canvas.width,
        y: Math.random() * (canvas.height - 150),
        size: 1 + Math.random() * 2,
        speedX: -0.2 - Math.random() * 0.3,
        speedY: Math.sin(Date.now() / 1000) * 0.2,
        alpha: 0.2 + Math.random() * 0.3
      });
    }
    // Update existing particles
    data.environmentParticles = data.environmentParticles.filter(p => {
      p.x += p.speedX;
      p.y += p.speedY + Math.sin(Date.now() / 500 + p.x * 0.01) * 0.1;
      return p.x > cameraX - 50;
    });
  };

  // Draw environment particles — extracted to game/gameEffects.js (P-1 refactor)
  const drawEnvironmentParticles = (ctx, cameraX) =>
    drawEnvironmentParticlesFn(ctx, gameDataRef.current, cameraX);

  // Draw muzzle flash — extracted to game/gameEffects.js (P-1 refactor)
  const drawMuzzleFlash = (ctx, cameraX) =>
    drawMuzzleFlashFn(ctx, gameDataRef.current, cameraX);

  // Create enemy death animation based on type
  const createDeathAnimation = (enemy, x, y) => {
    const data = gameDataRef.current;
    
    const deathAnim = {
      type: enemy.type,
      x: x,
      y: y,
      width: enemy.width,
      height: enemy.height,
      color: enemy.color || '#5AC8FA',
      timer: 0,
      maxTimer: 60, // 1 second animation
      phase: 0,
      parts: [],
      isBoss: enemy.isBoss,
      bossType: enemy.bossType
    };

    // Create debris parts based on enemy type
    switch (enemy.type) {
      case 'soldier':
      case 'grunt':
      case 'runner':
      case 'jumper':
      case 'commando':
      case 'ninja':
      case 'berserker':
      case 'elite':
        // Humanoid death - body parts flying
        deathAnim.parts = [
          { type: 'head', x: 0, y: -10, vx: (Math.random() - 0.5) * 6, vy: -8, rot: 0, rotSpeed: 0.3, size: 20, color: '#FFCC99' },
          { type: 'body', x: 0, y: 10, vx: (Math.random() - 0.5) * 4, vy: -5, rot: 0, rotSpeed: 0.2, size: 25, color: deathAnim.color },
          { type: 'arm', x: -15, y: 5, vx: -4, vy: -6, rot: 0, rotSpeed: 0.4, size: 15, color: deathAnim.color },
          { type: 'arm', x: 15, y: 5, vx: 4, vy: -7, rot: 0, rotSpeed: -0.4, size: 15, color: deathAnim.color },
          { type: 'leg', x: -8, y: 25, vx: -2, vy: -4, rot: 0, rotSpeed: 0.25, size: 18, color: deathAnim.color },
          { type: 'leg', x: 8, y: 25, vx: 2, vy: -3, rot: 0, rotSpeed: -0.25, size: 18, color: deathAnim.color },
          { type: 'gun', x: 20, y: 10, vx: 5, vy: -9, rot: 0, rotSpeed: 0.5, size: 12, color: '#333' }
        ];
        deathAnim.maxTimer = 45;
        break;

      case 'tank':
        // Tank death - turret pops off, treads fly
        deathAnim.parts = [
          { type: 'turret', x: 0, y: -15, vx: (Math.random() - 0.5) * 3, vy: -12, rot: 0, rotSpeed: 0.15, size: 30, color: '#5AC8FA' },
          { type: 'cannon', x: 20, y: -10, vx: 6, vy: -8, rot: 0, rotSpeed: 0.4, size: 25, color: '#333' },
          { type: 'tread', x: -20, y: 15, vx: -5, vy: -4, rot: 0, rotSpeed: 0.2, size: 35, color: '#222' },
          { type: 'tread', x: 20, y: 15, vx: 5, vy: -3, rot: 0, rotSpeed: -0.2, size: 35, color: '#222' },
          { type: 'wheel', x: -15, y: 20, vx: -6, vy: -6, rot: 0, rotSpeed: 0.8, size: 10, color: '#444' },
          { type: 'wheel', x: 15, y: 20, vx: 6, vy: -5, rot: 0, rotSpeed: -0.8, size: 10, color: '#444' }
        ];
        deathAnim.maxTimer = 60;
        break;

      case 'helicopter':
      case 'gunship':
        // Helicopter death - spiral down with smoke, rotor flies off
        deathAnim.parts = [
          { type: 'rotor', x: 0, y: -15, vx: 8, vy: -10, rot: 0, rotSpeed: 1.5, size: 40, color: '#888' },
          { type: 'tail', x: -30, y: 0, vx: -6, vy: -4, rot: 0, rotSpeed: 0.3, size: 25, color: '#3B8AC8' },
          { type: 'body', x: 0, y: 0, vx: -2, vy: 3, rot: 0, rotSpeed: 0.1, size: 35, color: '#5AC8FA', spiral: true }
        ];
        deathAnim.maxTimer = 90; // Longer for dramatic spiral
        break;

      case 'drone':
        // Drone death - sparks and spinning
        deathAnim.parts = [
          { type: 'core', x: 0, y: 0, vx: 0, vy: 2, rot: 0, rotSpeed: 2, size: 15, color: '#9945FF', sparks: true },
          { type: 'blade', x: -10, y: 0, vx: -8, vy: -6, rot: 0, rotSpeed: 1.2, size: 12, color: '#666' },
          { type: 'blade', x: 10, y: 0, vx: 8, vy: -5, rot: 0, rotSpeed: -1.2, size: 12, color: '#666' }
        ];
        deathAnim.maxTimer = 40;
        break;

      case 'mech':
        // Mech death - dramatic explosion with limbs
        deathAnim.parts = [
          { type: 'head', x: 0, y: -25, vx: (Math.random() - 0.5) * 4, vy: -15, rot: 0, rotSpeed: 0.2, size: 26, color: '#333' },
          { type: 'body', x: 0, y: 0, vx: 0, vy: -2, rot: 0, rotSpeed: 0.05, size: 40, color: '#5AC8FA', explosion: true },
          { type: 'arm', x: -20, y: -5, vx: -7, vy: -8, rot: 0, rotSpeed: 0.35, size: 20, color: '#3B8AC8' },
          { type: 'arm', x: 20, y: -5, vx: 7, vy: -9, rot: 0, rotSpeed: -0.35, size: 20, color: '#3B8AC8' },
          { type: 'leg', x: -12, y: 20, vx: -3, vy: -5, rot: 0, rotSpeed: 0.2, size: 30, color: '#3B8AC8' },
          { type: 'leg', x: 12, y: 20, vx: 3, vy: -4, rot: 0, rotSpeed: -0.2, size: 30, color: '#3B8AC8' }
        ];
        deathAnim.maxTimer = 70;
        break;

      default:
        // Default soldier-like death
        if (enemy.isBoss) {
          // Boss death - massive multi-stage explosion
          deathAnim.parts = [
            { type: 'core', x: 0, y: 0, vx: 0, vy: 0, rot: 0, rotSpeed: 0, size: 80, color: enemy.secondaryColor || '#9945FF', explosion: true, multiExplosion: true },
            { type: 'debris', x: -40, y: -20, vx: -8, vy: -12, rot: 0, rotSpeed: 0.3, size: 30, color: enemy.color },
            { type: 'debris', x: 40, y: -20, vx: 8, vy: -10, rot: 0, rotSpeed: -0.3, size: 30, color: enemy.color },
            { type: 'debris', x: -30, y: 30, vx: -6, vy: -8, rot: 0, rotSpeed: 0.25, size: 25, color: enemy.color },
            { type: 'debris', x: 30, y: 30, vx: 6, vy: -7, rot: 0, rotSpeed: -0.25, size: 25, color: enemy.color },
            { type: 'debris', x: 0, y: -40, vx: 0, vy: -15, rot: 0, rotSpeed: 0.4, size: 35, color: enemy.color }
          ];
          deathAnim.maxTimer = 120; // 2 seconds for boss
        } else {
          deathAnim.parts = [
            { type: 'body', x: 0, y: 0, vx: (Math.random() - 0.5) * 4, vy: -6, rot: 0, rotSpeed: 0.3, size: 30, color: deathAnim.color }
          ];
          deathAnim.maxTimer = 30;
        }
    }

    data.deathAnimations.push(deathAnim);
  };

  // Update death animations
  const updateDeathAnimations = (canvasHeight) => {
    const data = gameDataRef.current;
    const gravity = 0.4;

    data.deathAnimations = data.deathAnimations.filter(anim => {
      anim.timer++;
      
      // Update each part
      anim.parts.forEach(part => {
        part.x += part.vx;
        part.y += part.vy;
        part.vy += gravity;
        part.rot += part.rotSpeed;
        
        // Spiral effect for helicopters
        if (part.spiral) {
          part.vx = Math.sin(anim.timer * 0.2) * 3;
          part.rot += 0.05;
        }
        
        // Create sparks for sparking parts
        if (part.sparks && anim.timer % 3 === 0) {
          createSparks(anim.x + part.x, anim.y + part.y, Math.random() > 0.5 ? 1 : -1, '#FFFF00', 3);
        }
        
        // Create explosions for exploding parts
        if (part.explosion && anim.timer % 8 === 0) {
          createExplosion(anim.x + part.x + (Math.random() - 0.5) * part.size, 
                         anim.y + part.y + (Math.random() - 0.5) * part.size, 
                         '#FF9500', 5);
        }
        
        // Multi-explosion for boss death
        if (part.multiExplosion && anim.timer % 5 === 0) {
          const offsetX = (Math.random() - 0.5) * 100;
          const offsetY = (Math.random() - 0.5) * 80;
          createExplosion(anim.x + offsetX, anim.y + offsetY, 
                         Math.random() > 0.5 ? '#FF9500' : '#FF3B30', 12);
          triggerScreenShake(4, 6);
        }
      });
      
      // Remove when animation is done
      return anim.timer < anim.maxTimer;
    });
  };

  // Draw death animations
  const drawDeathAnimations = (ctx, cameraX, canvasHeight) => {
    const data = gameDataRef.current;
    
    data.deathAnimations.forEach(anim => {
      const progress = anim.timer / anim.maxTimer;
      const fadeAlpha = 1 - Math.pow(progress, 2); // Quadratic fade out
      
      ctx.save();
      ctx.globalAlpha = fadeAlpha;
      
      anim.parts.forEach(part => {
        const screenX = anim.x + part.x - cameraX;
        const screenY = anim.y + part.y;
        
        ctx.save();
        ctx.translate(screenX, screenY);
        ctx.rotate(part.rot);
        
        // Draw based on part type
        switch (part.type) {
          case 'head':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size/2, -part.size/2, part.size, part.size);
            // Eyes
            ctx.fillStyle = '#000';
            ctx.fillRect(-part.size/4, -part.size/6, 3, 3);
            ctx.fillRect(part.size/6, -part.size/6, 3, 3);
            break;
            
          case 'body':
          case 'debris':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size/2, -part.size/2, part.size, part.size * 0.8);
            break;
            
          case 'arm':
          case 'leg':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size/4, -part.size/2, part.size/2, part.size);
            break;
            
          case 'gun':
          case 'cannon':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size/2, -part.size/4, part.size, part.size/2);
            break;
            
          case 'turret':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size/2, -part.size/3, part.size, part.size * 0.6);
            break;
            
          case 'tread':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size/2, -part.size/4, part.size, part.size/2);
            // Tread marks
            ctx.fillStyle = '#444';
            for (let i = 0; i < 4; i++) {
              ctx.fillRect(-part.size/2 + 5 + i * 8, -part.size/6, 4, part.size/3);
            }
            break;
            
          case 'wheel':
            ctx.fillStyle = part.color;
            ctx.beginPath();
            ctx.arc(0, 0, part.size, 0, Math.PI * 2);
            ctx.fill();
            // Spokes
            ctx.strokeStyle = '#333';
            ctx.lineWidth = 2;
            for (let i = 0; i < 4; i++) {
              ctx.beginPath();
              ctx.moveTo(0, 0);
              ctx.lineTo(Math.cos(i * Math.PI/2) * part.size, Math.sin(i * Math.PI/2) * part.size);
              ctx.stroke();
            }
            break;
            
          case 'rotor':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size, -2, part.size * 2, 4);
            ctx.fillRect(-2, -part.size, 4, part.size * 2);
            break;
            
          case 'tail':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size/2, -part.size/6, part.size, part.size/3);
            // Tail rotor
            ctx.fillRect(part.size/2 - 3, -part.size/3, 6, part.size * 0.6);
            break;
            
          case 'core':
            // Glowing core
            const glowSize = part.size * (1 + Math.sin(anim.timer * 0.5) * 0.2);
            ctx.fillStyle = part.color;
            ctx.shadowColor = part.color;
            ctx.shadowBlur = 20;
            ctx.beginPath();
            ctx.arc(0, 0, glowSize/2, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
            break;
            
          case 'blade':
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size, -2, part.size * 2, 4);
            break;
            
          default:
            ctx.fillStyle = part.color;
            ctx.fillRect(-part.size/2, -part.size/2, part.size, part.size);
        }
        
        ctx.restore();
      });
      
      // Draw smoke trail for helicopters
      if ((anim.type === 'helicopter' || anim.type === 'gunship') && anim.timer % 4 === 0) {
        const smokeX = anim.x + anim.parts[2]?.x - cameraX || anim.x - cameraX;
        const smokeY = anim.y + anim.parts[2]?.y || anim.y;
        ctx.fillStyle = `rgba(100, 100, 100, ${0.4 * fadeAlpha})`;
        ctx.beginPath();
        ctx.arc(smokeX, smokeY, 8 + Math.random() * 4, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.restore();
    });
  };

  const applyPowerUp = useCallback((powerUp) => {
    const data = gameDataRef.current;
    const player = data.player;
    
    switch (powerUp.type) {
      case POWER_UP_TYPES.RAPID_FIRE:
        player.rapidFire = true;
        player.rapidFireTimer = 600; // 10 seconds at 60fps
        break;
      case POWER_UP_TYPES.SHIELD:
        player.hasShield = true;
        player.shieldTimer = 480; // 8 seconds
        break;
      case POWER_UP_TYPES.HEALTH:
        player.health = Math.min(player.maxHealth, player.health + 30);
        break;
      case POWER_UP_TYPES.GRENADE:
        player.grenades += 3;
        setGameState(prev => ({ ...prev, grenades: player.grenades }));
        break;
      case POWER_UP_TYPES.SPEED:
        player.speedBoost = true;
        player.speedTimer = 480;
        break;
      case POWER_UP_TYPES.SPREAD_SHOT:
        player.spreadShot = true;
        player.spreadShotTimer = 420; // 7 seconds
        break;
      case POWER_UP_TYPES.DOUBLE_DAMAGE:
        player.doubleDamage = true;
        player.doubleDamageTimer = 600; // 10 seconds
        break;
      case POWER_UP_TYPES.FREEZE:
        player.freezeActive = true;
        // Apply freeze duration bonus (Arctic Sentinel)
        let freezeDuration = 300; // 5 seconds base
        if (player.nftBonuses?.freeze_duration) {
          freezeDuration *= (1 + player.nftBonuses.freeze_duration / 100);
        }
        player.freezeTimer = freezeDuration;
        // Freeze all current enemies
        data.enemies.forEach(enemy => {
          enemy.frozen = true;
          enemy.frozenTimer = freezeDuration;
        });
        break;
      case POWER_UP_TYPES.MAGNET:
        player.magnetActive = true;
        player.magnetTimer = 600; // 10 seconds - attracts power-ups
        break;
      case POWER_UP_TYPES.LASER:
        player.laserActive = true;
        player.laserTimer = 360; // 6 seconds - piercing shots
        break;
      case POWER_UP_TYPES.EXPLOSIVE_ROUNDS:
        player.explosiveRounds = true;
        player.explosiveRoundsTimer = 420; // 7 seconds
        break;
      case POWER_UP_TYPES.EXTRA_LIFE:
        // Grant +1 life. Write the authoritative counter on the ref first so
        // an immediate same-frame death still sees the new value (React state
        // updates are async and would be missed by the stale closure).
        gameDataRef.current.lives = (gameDataRef.current.lives || 0) + 1;
        setGameState(prev => ({ ...prev, lives: gameDataRef.current.lives }));
        break;
      default:
        break;
    }
    
    soundManager.playPowerUp();
  }, []);

  // Draw other players in multiplayer mode
  const drawOtherPlayer = useCallback((ctx, playerState, cameraX, canvasHeight) => {
    const screenX = playerState.x - cameraX;
    const width = 50;
    const height = 75;
    
    // Skip if off-screen
    if (screenX < -width || screenX > ctx.canvas.width + width) return;
    
    ctx.save();
    ctx.globalAlpha = 0.9;
    
    // Apply depth-based perspective scaling
    const otherGroundY = playerState.groundY !== undefined ? playerState.groundY : DEFAULT_GROUND_Y;
    const scale = getDepthScale(otherGroundY);
    const cx = screenX + width / 2;
    const cy = playerState.y + height / 2;
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    ctx.translate(-cx, -cy);
    
    // Flip based on direction
    if (playerState.direction === -1) {
      ctx.scale(-1, 1);
    }
    
    const drawX = playerState.direction === -1 ? -screenX - width : screenX;
    const drawY = playerState.y;
    
    // Check NFT type for specialized rendering
    if (playerState.nftId === 'nft_foot_stabber') {
      drawOtherFootStabber(ctx, drawX, drawY, width, height, playerState.direction);
    } else if (playerState.nftId === 'nft_ofts') {
      drawOtherOFTS(ctx, drawX, drawY, width, height, playerState.direction);
    } else if (playerState.nftId && playerState.nftId.startsWith('nft_')) {
      drawOtherNFTCharacter(ctx, drawX, drawY, width, height, playerState.nftId);
    } else {
      // Draw generic Lego character
      const color = playerState.color || '#FF3B30';
      
      // Body
      ctx.fillStyle = color;
      ctx.fillRect(drawX + 5, drawY + 25, 40, 30);
      
      // Head
      ctx.fillStyle = '#FFCC99';
      ctx.fillRect(drawX + 10, drawY + 5, 30, 22);
      
      // Eyes
      ctx.fillStyle = '#000';
      ctx.fillRect(drawX + 16, drawY + 11, 4, 5);
      ctx.fillRect(drawX + 28, drawY + 11, 4, 5);
      
      // Legs
      ctx.fillStyle = color;
      ctx.fillRect(drawX + 10, drawY + 55, 12, 18);
      ctx.fillRect(drawX + 28, drawY + 55, 12, 18);
      
      // Arms
      ctx.fillRect(drawX, drawY + 28, 8, 20);
      ctx.fillRect(drawX + 42, drawY + 28, 8, 20);
    }
    
    ctx.restore();
    
    // Health bar above player (always draw after restore)
    const healthPercent = playerState.health / 100;
    const barWidth = 40;
    const barX = screenX + (width - barWidth) / 2;
    const barY = playerState.y - 15;
    
    ctx.fillStyle = '#333';
    ctx.fillRect(barX, barY, barWidth, 6);
    ctx.fillStyle = '#9945FF'; // Purple for other players
    ctx.fillRect(barX, barY, barWidth * healthPercent, 6);
    
    // Player indicator
    ctx.fillStyle = '#9945FF';
    ctx.font = '8px "Press Start 2P", monospace';
    ctx.textAlign = 'center';
    ctx.fillText('P2', screenX + width / 2, playerState.y - 22);
  }, []);
  
  // Draw Foot Stabber for other player
  const drawOtherFootStabber = (ctx, x, y, width, height, direction) => {
    const greyBody = '#808080';
    const black = '#1a1a1a';
    const skinTone = '#FFCC99';
    const white = '#FFFFFF';
    const gold = '#FFD700';
    
    // Legs (grey pants)
    ctx.fillStyle = '#5a5a5a';
    ctx.fillRect(x + 12, y + 50, 10, 22);
    ctx.fillRect(x + 28, y + 50, 10, 22);
    
    // Shoes (black)
    ctx.fillStyle = black;
    ctx.fillRect(x + 10, y + 68, 14, 6);
    ctx.fillRect(x + 26, y + 68, 14, 6);
    
    // Body (grey with black blazer)
    ctx.fillStyle = greyBody;
    ctx.fillRect(x + 8, y + 28, 34, 26);
    
    // Black Blazer lapels
    ctx.fillStyle = black;
    ctx.beginPath();
    ctx.moveTo(x + 10, y + 28);
    ctx.lineTo(x + 22, y + 28);
    ctx.lineTo(x + 18, y + 45);
    ctx.lineTo(x + 10, y + 54);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x + 28, y + 28);
    ctx.lineTo(x + 40, y + 28);
    ctx.lineTo(x + 40, y + 54);
    ctx.lineTo(x + 32, y + 45);
    ctx.closePath();
    ctx.fill();
    
    // Arms (black blazer sleeves)
    ctx.fillStyle = black;
    ctx.fillRect(x + 2, y + 30, 8, 22);
    ctx.fillRect(x + 40, y + 30, 8, 22);
    ctx.fillStyle = white;
    ctx.fillRect(x + 3, y + 48, 6, 6);
    ctx.fillRect(x + 41, y + 48, 6, 6);
    
    // Head
    ctx.fillStyle = skinTone;
    ctx.fillRect(x + 12, y + 8, 26, 22);
    
    // Eyes
    ctx.fillStyle = black;
    ctx.fillRect(x + 18, y + 14, 4, 5);
    ctx.fillRect(x + 30, y + 14, 4, 5);
    
    // Top hat
    ctx.fillStyle = black;
    ctx.fillRect(x + 8, y + 6, 34, 4);
    ctx.fillRect(x + 14, y - 18, 22, 26);
    ctx.fillStyle = '#8B0000';
    ctx.fillRect(x + 14, y + 2, 22, 4);
  };
  
  // Draw OFTS for other player
  const drawOtherOFTS = (ctx, x, y, width, height, direction) => {
    const camoBase = '#4A5568';
    const camoDark = '#2D3748';
    const black = '#1a1a1a';
    const skinTone = '#FFCC99';
    const gold = '#FFD700';
    
    // Legs (camo)
    ctx.fillStyle = camoBase;
    ctx.fillRect(x + 12, y + 50, 10, 18);
    ctx.fillRect(x + 28, y + 50, 10, 18);
    ctx.fillStyle = camoDark;
    ctx.fillRect(x + 14, y + 52, 4, 4);
    ctx.fillRect(x + 30, y + 55, 4, 4);
    
    // Boots
    ctx.fillStyle = black;
    ctx.fillRect(x + 10, y + 66, 14, 8);
    ctx.fillRect(x + 26, y + 66, 14, 8);
    
    // Body (camo)
    ctx.fillStyle = camoBase;
    ctx.fillRect(x + 8, y + 28, 34, 26);
    ctx.fillStyle = camoDark;
    ctx.fillRect(x + 12, y + 32, 6, 6);
    ctx.fillRect(x + 26, y + 38, 8, 5);
    ctx.fillRect(x + 18, y + 44, 5, 6);
    
    // Tactical vest
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.fillRect(x + 10, y + 30, 30, 22);
    
    // Arms (camo)
    ctx.fillStyle = camoBase;
    ctx.fillRect(x + 2, y + 30, 8, 20);
    ctx.fillRect(x + 40, y + 30, 8, 20);
    
    // Machine gun
    ctx.fillStyle = '#333';
    ctx.fillRect(x + 46, y + 40, 18, 6);
    ctx.fillStyle = '#FF4500';
    ctx.fillRect(x + 62, y + 41, 4, 4);
    
    // Head
    ctx.fillStyle = skinTone;
    ctx.fillRect(x + 12, y + 8, 26, 22);
    // Camo face paint
    ctx.fillStyle = camoDark;
    ctx.fillRect(x + 14, y + 12, 6, 3);
    ctx.fillRect(x + 28, y + 16, 6, 3);
    
    // Eyes
    ctx.fillStyle = black;
    ctx.fillRect(x + 18, y + 14, 4, 5);
    ctx.fillRect(x + 30, y + 14, 4, 5);
    
    // Monocle
    ctx.strokeStyle = gold;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x + 32, y + 16, 5, 0, Math.PI * 2);
    ctx.stroke();
    
    // Top hat
    ctx.fillStyle = black;
    ctx.fillRect(x + 8, y + 6, 34, 4);
    ctx.fillRect(x + 14, y - 18, 22, 26);
    ctx.fillStyle = '#8B0000';
    ctx.fillRect(x + 14, y + 2, 22, 4);
  };

  // Unified multiplayer rendering for all themed NFT characters
  const drawOtherNFTCharacter = (ctx, x, y, w, h, nftId) => {
    // NFT theme configs: [bodyColor, accentColor, headDetail]
    const themes = {
      'nft_cyber_phantom':    { body: '#0a0a1a', accent: '#00FFFF', eye: '#00FFFF', headStyle: 'visor' },
      'nft_molten_guardian':  { body: '#3D1400', accent: '#FF4500', eye: '#FF4500', headStyle: 'horns' },
      'nft_arctic_sentinel':  { body: '#6BB8D4', accent: '#fff',    eye: '#fff',    headStyle: 'crown' },
      'nft_shadow_assassin':  { body: '#0d0d1a', accent: '#9945FF', eye: '#9945FF', headStyle: 'hood' },
      'nft_nature_spirit':    { body: '#5C3A1E', accent: '#228B22', eye: '#228B22', headStyle: 'leaves' },
      'nft_void_walker':      { body: '#0d0020', accent: '#8B00FF', eye: '#8B00FF', headStyle: 'runes' },
      'nft_solar_knight':     { body: '#DAA520', accent: '#FFD700', eye: '#FFD700', headStyle: 'crest' },
      'nft_thunder_lord':     { body: '#1a1a4a', accent: '#FFFF00', eye: '#FFFF00', headStyle: 'spikes' },
    };
    const theme = themes[nftId] || { body: '#5AC8FA', accent: '#fff', eye: '#000', headStyle: 'none' };

    // Legs
    ctx.fillStyle = theme.body;
    ctx.fillRect(x + 12, y + 52, 10, 20); ctx.fillRect(x + 28, y + 52, 10, 20);
    // Accent strips on legs
    ctx.fillStyle = theme.accent;
    ctx.fillRect(x + 14, y + 56, 2, 12); ctx.fillRect(x + 30, y + 56, 2, 12);
    // Boots
    ctx.fillRect(x + 10, y + 70, 14, 4); ctx.fillRect(x + 26, y + 70, 14, 4);
    // Body
    ctx.fillStyle = theme.body; ctx.fillRect(x + 8, y + 28, 34, 26);
    // Accent detail on body
    ctx.fillStyle = theme.accent; ctx.globalAlpha = 0.6;
    ctx.fillRect(x + 20, y + 34, 10, 14); ctx.globalAlpha = 1;
    // Core/emblem
    ctx.beginPath(); ctx.arc(x + 25, y + 41, 4, 0, Math.PI * 2); ctx.fill();
    // Arms
    ctx.fillStyle = theme.body;
    ctx.fillRect(x + 2, y + 30, 8, 20); ctx.fillRect(x + 40, y + 30, 8, 20);
    ctx.fillStyle = theme.accent;
    ctx.fillRect(x + 3, y + 48, 6, 4); ctx.fillRect(x + 41, y + 48, 6, 4);
    // Gun
    ctx.fillStyle = '#333'; ctx.fillRect(x + 46, y + 40, 14, 5);
    ctx.fillStyle = theme.accent; ctx.fillRect(x + 58, y + 41, 4, 3);
    // Head
    ctx.fillStyle = theme.body; ctx.fillRect(x + 12, y + 6, 26, 24);
    // Eyes
    ctx.fillStyle = theme.eye;
    ctx.fillRect(x + 18, y + 14, 4, 5); ctx.fillRect(x + 28, y + 14, 4, 5);

    // Head detail based on style
    ctx.fillStyle = theme.accent;
    switch (theme.headStyle) {
      case 'visor': // Cyber Phantom
        ctx.fillRect(x + 14, y + 14, 22, 8); // Full visor
        ctx.fillStyle = '#fff'; ctx.fillRect(x + 20, y + 16, 4, 4); break;
      case 'horns': // Molten Guardian
        ctx.fillStyle = '#1a0a00';
        ctx.beginPath(); ctx.moveTo(x + 12, y + 10); ctx.lineTo(x + 8, y - 6); ctx.lineTo(x + 16, y + 6); ctx.fill();
        ctx.beginPath(); ctx.moveTo(x + 38, y + 10); ctx.lineTo(x + 42, y - 6); ctx.lineTo(x + 34, y + 6); ctx.fill(); break;
      case 'crown': // Arctic Sentinel
        for (let i = 0; i < 5; i++) {
          ctx.beginPath(); ctx.moveTo(x + 13 + i * 6, y + 8);
          ctx.lineTo(x + 16 + i * 6, y - 2 - (i === 2 ? 4 : 0));
          ctx.lineTo(x + 19 + i * 6, y + 8); ctx.fill();
        } break;
      case 'hood': // Shadow Assassin
        ctx.fillStyle = '#0d0d1a'; ctx.fillRect(x + 10, y + 4, 30, 12); break;
      case 'leaves': // Nature Spirit
        ctx.fillStyle = '#228B22';
        for (let i = 0; i < 4; i++) {
          ctx.beginPath(); ctx.ellipse(x + 13 + i * 7, y + 6, 5, 3, 0, 0, Math.PI * 2); ctx.fill();
        } break;
      case 'runes': // Void Walker
        ctx.fillRect(x + 16, y + 8, 3, 3); ctx.fillRect(x + 31, y + 8, 3, 3);
        ctx.beginPath(); ctx.arc(x + 25, y + 6, 2, 0, Math.PI * 2); ctx.fill(); break;
      case 'crest': // Solar Knight
        ctx.fillStyle = '#FF8C00';
        ctx.beginPath(); ctx.moveTo(x + 20, y + 8); ctx.lineTo(x + 25, y - 4);
        ctx.lineTo(x + 30, y + 8); ctx.fill();
        ctx.fillStyle = '#FFD700'; ctx.beginPath(); ctx.arc(x + 25, y + 4, 3, 0, Math.PI * 2); ctx.fill(); break;
      case 'spikes': // Thunder Lord
        ctx.beginPath(); ctx.moveTo(x + 14, y + 8); ctx.lineTo(x + 17, y - 4); ctx.lineTo(x + 20, y + 8); ctx.fill();
        ctx.beginPath(); ctx.moveTo(x + 22, y + 8); ctx.lineTo(x + 25, y - 8); ctx.lineTo(x + 28, y + 8); ctx.fill();
        ctx.beginPath(); ctx.moveTo(x + 30, y + 8); ctx.lineTo(x + 33, y - 4); ctx.lineTo(x + 36, y + 8); ctx.fill(); break;
      default: break;
    }
  };

  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const data = gameDataRef.current;
    const { player, enemies, bullets, particles, powerUps, keys } = data;

    // Check player exists and game should be running
    if (!player || !data.gameRunning) {
      // Clear the animation frame reference when not running
      animationFrameRef.current = null;
      return;
    }

    // Revival slow-mo: during the brief revival window, halve entity update rate
    // (player still runs full speed for responsiveness)
    data._frameTick = (data._frameTick || 0) + 1;
    const inRevival = data.revivalEffect.active && data.revivalEffect.timer > 0;
    const skipEntityTick = inRevival && (data._frameTick % 2 === 0);

    // === MULTIPLAYER HOST/GUEST ROLE ===
    const mpRole = multiplayerSyncRef.current;
    const isMultiplayer = !!mpRole;
    const isHost = isMultiplayer && !!mpRole.isHost;
    const isGuest = isMultiplayer && !mpRole.isHost;

    // Guest reconciliation: rebuild local enemy list from host's authoritative
    // syncedEnemies snapshot. New ids -> spawn (snap to target); existing ->
    // update _target* fields and let the lerp pass smoothly move e.x/e.y/e.groundY/e.jumpZ
    // toward them. Health/animFrame/phase apply directly (instant).
    // NOTE: we mutate `data.enemies` IN PLACE (length=0 + push) instead of reassigning
    // because the gameLoop body destructured `enemies` from `data` earlier — reassignment
    // would leave the local const pointing at a stale array.
    if (isGuest && mpRole.syncedEnemies && Array.isArray(mpRole.syncedEnemies)) {
      const snapshot = mpRole.syncedEnemies;
      const localById = new Map(data.enemies.map(e => [e.id, e]));
      const next = [];
      for (const s of snapshot) {
        if (!s || !s.id) continue;
        let e = localById.get(s.id);
        const isNew = !e;
        if (!e) {
          e = new Enemy(s.x ?? 0, 0, s.type || 'soldier', s.difficulty || 1, s.level || gameState.level || 1);
          e.id = s.id;
          // Snap new spawns directly to their authoritative position so we
          // don't see them lerp in from off-screen.
          e.x = s.x;
          e.y = s.y;
          e.groundY = s.groundY ?? 0;
          e.jumpZ = s.jumpZ ?? 0;
        }
        // Lerp targets (smoothed every frame) — see lerp pass below
        e._targetX = s.x;
        e._targetY = s.y;
        e._targetGroundY = s.groundY ?? e.groundY;
        e._targetJumpZ = s.jumpZ ?? 0;
        // Direct (instant) authoritative fields
        e.health = s.health;
        e.maxHealth = s.maxHealth ?? e.maxHealth;
        e.velocityX = s.velocityX ?? e.velocityX;
        e.phase = s.phase ?? 1;
        e.isDead = !!s.isDead;
        // animFrame: take snapshot value but allow local advance between snapshots
        if (isNew && s.animFrame !== undefined) e.animFrame = s.animFrame;
        if (s.isBoss) {
          e.isBoss = true;
          if (s.bossName) e.bossName = s.bossName;
        }
        next.push(e);
      }
      data.enemies.length = 0;
      data.enemies.push(...next);

      // === LERP PASS ===
      // Smoothly move each enemy toward its target each frame. Factor 0.28 gives
      // ~85% convergence within 6 frames (one snapshot interval @ 10Hz, 60fps).
      const LERP = 0.28;
      for (const e of data.enemies) {
        if (e._targetX !== undefined) e.x += (e._targetX - e.x) * LERP;
        if (e._targetY !== undefined) e.y += (e._targetY - e.y) * LERP;
        if (e._targetGroundY !== undefined) e.groundY += (e._targetGroundY - e.groundY) * LERP;
        if (e._targetJumpZ !== undefined) e.jumpZ += (e._targetJumpZ - e.jumpZ) * LERP;
        // Keep animation frame ticking smoothly between snapshots
        e.animFrame = (e.animFrame + 0.1) % 4;
      }
    }
    // Apply authoritative game-level state from host (explicit, not derived).
    // Mirror bossActive / bossSpawned / levelProgress so guest UI matches host.
    if (isGuest && mpRole.syncedHostState) {
      const hs = mpRole.syncedHostState;
      if (typeof hs.levelProgress === 'number') {
        data.levelProgress = hs.levelProgress;
      }
      if (hs.bossSpawned !== undefined) {
        data.bossSpawned = !!hs.bossSpawned;
      }
      // Sync guest's data.level from host so bg/ground/enemies-palette all
      // reflect the current level even before React state flushes.
      if (typeof hs.level === 'number' && hs.level >= 1) {
        data.level = hs.level;
      }
      const targetBossActive = !!hs.bossActive;
      const targetBossDefeated = !!hs.bossDefeated;
      if (gameState.bossActive !== targetBossActive || gameState.bossDefeated !== targetBossDefeated) {
        setGameState(prev => ({
          ...prev,
          bossActive: targetBossActive,
          bossDefeated: targetBossDefeated
        }));
      }
    }

    // Guest reconciliation: rebuild local power-up list from host's snapshot.
    // Mutate in place (see enemy reconcile note above).
    if (isGuest && mpRole.syncedPowerUps && Array.isArray(mpRole.syncedPowerUps)) {
      const snapshot = mpRole.syncedPowerUps;
      const localById = new Map(data.powerUps.map(p => [p.id, p]));
      const next = [];
      for (const s of snapshot) {
        if (!s || !s.id) continue;
        let p = localById.get(s.id);
        if (!p) {
          p = new PowerUp(s.x, 0, s.type);
          p.id = s.id;
        }
        p.x = s.x;
        p.y = s.y;
        p.groundY = s.groundY ?? p.groundY;
        p.active = s.active !== false;
        next.push(p);
      }
      data.powerUps.length = 0;
      data.powerUps.push(...next);
    }
    // === END MULTIPLAYER HOST/GUEST ROLE ===

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Read level from ref (synchronous) — gameState.level is stale in this
    // closure between boss death and React re-render, causing the sky/ground
    // to freeze on the previous level. See gameDataRef.level init comment.
    const currentLevel = data.level || gameState.level;
    drawBackground(ctx, data.cameraX, canvas.width, canvas.height, currentLevel);

    player.update(canvas.height, keys);

    // Tournament timer countdown
    if (data.tournamentTimer > 0) {
      const now = Date.now();
      if (data.tournamentLastTick === 0) data.tournamentLastTick = now;
      if (now - data.tournamentLastTick >= 1000) {
        data.tournamentTimer -= 1;
        data.tournamentLastTick = now;
        setGameState(prev => ({ ...prev, tournamentTimeRemaining: data.tournamentTimer }));
        if (data.tournamentTimer <= 0) {
          // Time's up - end game
          data.gameRunning = false;
          soundManager.playGameOver();
          soundManager.stopMusic();
          setGameState(prev => ({ ...prev, isGameOver: true, isPlaying: false, tournamentTimeRemaining: 0 }));
          onGameOver?.({ score: gameState.score, level: gameState.level, kills: gameState.kills, max_kill_streak: data.maxKillStreak, bosses_defeated: data.bossesDefeated, kills_by_type: data.killsByType || {}, defeated_boss_types: Object.keys(data.defeatedBossTypes || {}), time_played: Math.floor((Date.now() - (data.gameStartTime || Date.now())) / 1000) });
        }
      }
    }

    // Create running dust particles when moving on ground
    if (player.isGrounded && (keys.left || keys.right || keys.up || keys.down)) {
      createRunningDust(player.x + player.width / 2, player.y + player.height);
    }

    // Camera follow
    const targetCameraX = player.x - canvas.width * 0.3;
    data.cameraX += (targetCameraX - data.cameraX) * 0.1;
    if (data.cameraX < 0) data.cameraX = 0;

    // Shooting - different behavior based on weapon
    const getShootCooldown = () => {
      let cooldown;
      if (player.rapidFire) cooldown = 100;
      else if (player.currentWeapon === WEAPON_TYPES.ROCKET_LAUNCHER) cooldown = 500;
      else if (player.currentWeapon === WEAPON_TYPES.MINI_TANK) cooldown = 300;
      else if (player.currentWeapon === WEAPON_TYPES.ASSAULT_RIFLE) cooldown = 130; // fast full-auto
      else cooldown = 200;
      
      // Apply fire rate bonus (OFTS machine gun mode)
      if (player.nftBonuses?.fire_rate_bonus) {
        cooldown = cooldown * (100 / (100 + player.nftBonuses.fire_rate_bonus));
      }
      
      return cooldown;
    };
    
    if (keys.shoot && Date.now() - data.lastShot > getShootCooldown()) {
      const bulletX = player.x + (player.direction === 1 ? player.width : 0);
      const bulletY = player.inTank ? player.y + 18 : player.y + 35;
      
      // Trigger muzzle flash for all shots
      triggerMuzzleFlash(bulletX + (player.direction * 10), bulletY);

      // Broadcast shoot action to peers in multiplayer (so they can see our tracers)
      const broadcastShoot = (kind) => {
        const mp = multiplayerSyncRef.current;
        if (!mp) return;
        mp.sendGameAction('shoot', {
          x: bulletX,
          y: bulletY,
          direction: player.direction,
          groundY: player.groundY,
          kind,
          color: playerColor,
          rapidFire: !!player.rapidFire,
          doubleDamage: !!player.doubleDamage,
          isLaser: !!player.laserActive,
          isExplosive: !!player.explosiveRounds
        });
      };

      if (player.currentWeapon === WEAPON_TYPES.ROCKET_LAUNCHER && player.rocketAmmo > 0) {
        // Fire rocket
        const rocketBullet = new Bullet(bulletX, bulletY, player.direction, false, false, true, false);
        rocketBullet.groundY = player.groundY;
        bullets.push(rocketBullet);
        broadcastShoot('rocket');
        player.rocketAmmo--;
        setGameState(prev => ({ ...prev, rocketAmmo: player.rocketAmmo }));
        soundManager.playExplosion();
        triggerScreenShake(8, 15); // Big shake for rockets
        
        // Auto-switch weapon if out of ammo
        if (player.rocketAmmo <= 0) {
          player.currentWeapon = WEAPON_TYPES.DEFAULT;
          setGameState(prev => ({ ...prev, currentWeapon: 'default' }));
        }
      } else if (player.currentWeapon === WEAPON_TYPES.MINI_TANK && player.tankAmmo > 0) {
        // Fire tank shell
        const tankBullet = new Bullet(bulletX, bulletY, player.direction, false, false, false, true);
        tankBullet.groundY = player.groundY;
        bullets.push(tankBullet);
        broadcastShoot('tank');
        player.tankAmmo--;
        setGameState(prev => ({ ...prev, tankAmmo: player.tankAmmo }));
        soundManager.playShoot();
        triggerScreenShake(6, 10); // Medium shake for tank
        
        // Exit tank if out of ammo
        if (player.tankAmmo <= 0) {
          player.inTank = false;
          player.tankArmor = 0;
          player.currentWeapon = WEAPON_TYPES.DEFAULT;
          setGameState(prev => ({ ...prev, currentWeapon: 'default', tankAmmo: 0 }));
        }
      } else if (player.currentWeapon === WEAPON_TYPES.ASSAULT_RIFLE && player.rifleAmmo > 0) {
        // Fire Assault Rifle round - distinct neon-green tracer, faster bullets, +80% damage vs default
        const rifleBullet = new Bullet(bulletX, bulletY, player.direction, false, false, false, false, false, true);
        rifleBullet.groundY = player.groundY;
        rifleBullet.doubleDamage = player.doubleDamage; // still benefits from buffs
        bullets.push(rifleBullet);
        broadcastShoot('rifle');
        player.rifleAmmo--;
        setGameState(prev => ({ ...prev, rifleAmmo: player.rifleAmmo }));
        // Distinctive pop-pop-pop firing sound
        soundManager.playAssaultRifle();
        // Eject a brass casing from the side of the rifle (opposite firing direction)
        const ejectX = bulletX - player.direction * 4;
        const ejectY = bulletY - 4;
        data.brassCasings.push(new BrassCasing(ejectX, ejectY, player.direction));
        triggerScreenShake(2, 4); // light kick

        // Auto-switch to default if out of ammo
        if (player.rifleAmmo <= 0) {
          player.currentWeapon = WEAPON_TYPES.DEFAULT;
          setGameState(prev => ({ ...prev, currentWeapon: 'default' }));
        }
      } else {
        // Default weapon - handle various power-up effects
        const baseBullet = new Bullet(bulletX, bulletY, player.direction, false, false, false, false, player.rapidFire);
        baseBullet.groundY = player.groundY;
        
        // Apply power-up modifications
        baseBullet.isLaser = player.laserActive;
        baseBullet.isExplosive = player.explosiveRounds;
        baseBullet.doubleDamage = player.doubleDamage;
        
        if (player.spreadShot) {
          // Spread shot - fire 3 bullets in a fan pattern
          const spreadAngles = [-0.15, 0, 0.15];
          spreadAngles.forEach(angle => {
            const spreadBullet = new Bullet(bulletX, bulletY, player.direction, false, false, false, false, player.rapidFire);
            spreadBullet.groundY = player.groundY;
            spreadBullet.velocityY = Math.sin(angle) * 4;
            spreadBullet.isSpread = true;
            spreadBullet.isLaser = player.laserActive;
            spreadBullet.isExplosive = player.explosiveRounds;
            spreadBullet.doubleDamage = player.doubleDamage;
            bullets.push(spreadBullet);
          });
          broadcastShoot('spread');
          triggerScreenShake(3, 5); // Small shake for spread
        } else {
          bullets.push(baseBullet);
          broadcastShoot('default');
        }
        
        soundManager.playShoot();
      }
      
      // Apply combo damage multiplier to bullets fired this frame (P-1).
      // Only affects local bullets that haven't been boosted yet (`_comboBoosted` flag
      // prevents double-boost). Remote peer bullets are explicitly skipped.
      const comboTier = getComboTier(data.comboCount);
      if (comboTier.damageMult > 1) {
        for (const b of bullets) {
          if (b.isRemote || b._comboBoosted) continue;
          b.damage = Math.round(b.damage * comboTier.damageMult);
          b._comboBoosted = true;
        }
      }
      data.lastShot = Date.now();
    }

    // Weapon switching
    if (keys.switchWeapon && Date.now() - data.lastWeaponSwitch > 300) {
      const newWeapon = player.switchWeapon();
      if (newWeapon) {
        setGameState(prev => ({ 
          ...prev, 
          currentWeapon: player.currentWeapon,
          rocketAmmo: player.rocketAmmo,
          tankAmmo: player.tankAmmo,
          rifleAmmo: player.rifleAmmo
        }));
      }
      data.lastWeaponSwitch = Date.now();
      keys.switchWeapon = false;
    }

    // Grenade throwing
    if (keys.grenade && player.grenades > 0) {
      const grenadeX = player.x + player.width / 2;
      const grenadeY = player.y + 20;
      const grenadeBullet = new Bullet(grenadeX, grenadeY, player.direction, false, true);
      grenadeBullet.groundY = player.groundY;
      bullets.push(grenadeBullet);
      // Broadcast grenade so peers see the same arc + explosion
      const mpGrenade = multiplayerSyncRef.current;
      if (mpGrenade) {
        mpGrenade.sendGameAction('shoot', {
          x: grenadeX,
          y: grenadeY,
          direction: player.direction,
          groundY: player.groundY,
          kind: 'grenade',
          color: playerColor
        });
      }
      player.grenades--;
      player.startGrenadeThrow(); // Trigger throw animation
      setGameState(prev => ({ ...prev, grenades: player.grenades }));
      keys.grenade = false;
    }

    // Spawn enemies (host or single-player only — guests receive enemies via host sync)
    if (!isGuest) {
      data.spawnTimer++;
      const spawnRate = Math.max(60, 120 - (data.level || gameState.level) * 8);
      if (data.spawnTimer > spawnRate) {
        spawnEnemy(canvas.width, data.level || gameState.level);
        data.spawnTimer = 0;
      }
    }

    // Spawn power-ups (host or single-player only — guests receive via host sync)
    if (!isGuest) {
      data.powerUpTimer++;
      if (data.powerUpTimer > 400) {
        spawnPowerUp(canvas.width);
        data.powerUpTimer = 0;
      }
    }

    // Update enemies
    enemies.forEach((enemy, index) => {
      // Guest: skip enemy AI tick — host is authoritative and we lerp toward
      // host's snapshot positions. Animation frame is advanced in the lerp pass.
      if (!skipEntityTick && !isGuest) enemy.update(player.x, player.groundY, canvas.height);

      // Fire damage over time (OFTS incendiary bullets)
      if (enemy.onFire && enemy.fireTimer > 0) {
        enemy.fireTimer--;
        // Apply fire damage every second (60 frames)
        if (enemy.fireTimer % 60 === 0) {
          enemy.health -= enemy.fireDamage || 5;
          // Fire particle effect
          createExplosion(
            enemy.x + enemy.width / 2 + (Math.random() - 0.5) * 20, 
            enemy.y + (Math.random() * enemy.height), 
            '#FF4500', 
            4
          );
        }
        // Random fire particles
        if (Math.random() < 0.1) {
          createExplosion(
            enemy.x + enemy.width / 2 + (Math.random() - 0.5) * 15, 
            enemy.y + enemy.height * 0.3, 
            ['#FF4500', '#FF6B00', '#FFD700'][Math.floor(Math.random() * 3)], 
            2
          );
        }
        // Check if fire killed the enemy
        if (enemy.health <= 0 && !enemy.isDead) {
          enemy.isDead = true;
          createDeathAnimation(enemy, enemy.x + enemy.width / 2, enemy.y + enemy.height / 2);
          createExplosion(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2, '#FF4500', 18);
          const points = enemy.getScoreValue();
          // Fire was applied by `enemy.fireShooter` (set when incendiary bullet hit).
          // null/undefined => local; remote shooter wallet => attribute via WS.
          creditKill(points, enemy.fireShooter ? { isRemote: true, shooter: enemy.fireShooter } : null);
        }
        // Fire expires
        if (enemy.fireTimer <= 0) {
          enemy.onFire = false;
        }
      }

      // Enemy shooting - depth-aware: only shoot if within depth range or flying
      const depthClose = enemy.isFlying() || Math.abs(enemy.groundY - player.groundY) < BULLET_DEPTH_THRESHOLD;
      if (enemy.canShoot() && Math.abs(enemy.x - player.x) < 400 && depthClose) {
        const direction = enemy.x > player.x ? -1 : 1;
        const bulletY = enemy.y + enemy.height * 0.5;
        const b = new Bullet(enemy.x, bulletY, direction, true);
        b.groundY = enemy.groundY;
        bullets.push(b);
        enemy.shoot();
      }

      if (enemy.x < data.cameraX - 200) {
        enemy.markedForRemoval = true;
      }
    });
    
    // Remove enemies marked for removal (off-screen only here, deaths handled below)
    // CRITICAL: mutate in place (length=0 + push) — the loop body holds a
    // destructured `const { enemies } = data` reference at line 1127, so a
    // reassignment here would leave the local `enemies` pointing at a stale
    // array and the boss-defeated detection (line 2034) would never fire.
    {
      const _kept = data.enemies.filter(e => !e.markedForRemoval);
      data.enemies.length = 0;
      data.enemies.push(..._kept);
    }

    // === NFT ABILITY PROCESSING ===
    const nftBonuses = player.nftBonuses;
    if (nftBonuses) {
      // Burn Aura (Molten Guardian) - damage nearby enemies
      if (nftBonuses.burn_aura) {
        data.nftAbilityTimers.burnAuraTick++;
        if (data.nftAbilityTimers.burnAuraTick >= 60) { // Every second
          data.nftAbilityTimers.burnAuraTick = 0;
          enemies.forEach((enemy) => {
            const dist = Math.hypot(enemy.x - player.x, enemy.y - player.y);
            if (dist < 100) { // 100px range
              enemy.health -= nftBonuses.burn_aura;
              createExplosion(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2, '#FF4500', 3);
              if (enemy.health <= 0 && !enemy.isBoss && !enemy.isDead) {
                enemy.isDead = true;
                createDeathAnimation(enemy, enemy.x + enemy.width / 2, enemy.y + enemy.height / 2);
                createExplosion(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2, '#FF4500', 12);
                const points = enemy.getScoreValue();
                setGameState(prev => ({
                  ...prev,
                  score: prev.score + points,
                  kills: prev.kills + 1
                }));
              }
            }
          });
        }
      }

      // Slow Aura (Arctic Sentinel) - slow nearby enemies
      if (nftBonuses.slow_aura) {
        enemies.forEach(enemy => {
          const dist = Math.hypot(enemy.x - player.x, enemy.y - player.y);
          if (dist < 150) { // 150px range
            enemy.frozen = true;
            enemy.frozenTimer = 30; // Half second slow effect
          }
        });
      }

      // Health Regeneration (Nature Spirit) - heal over time
      if (nftBonuses.health_regen) {
        data.nftAbilityTimers.healthRegenTimer++;
        if (data.nftAbilityTimers.healthRegenTimer >= 300) { // Every 5 seconds
          data.nftAbilityTimers.healthRegenTimer = 0;
          if (player.health < player.maxHealth) {
            player.health = Math.min(player.maxHealth, player.health + nftBonuses.health_regen);
            createExplosion(player.x + player.width / 2, player.y + player.height / 2, '#228B22', 5);
          }
        }
      }

      // Detection Reduction (Shadow Assassin) - enemies don't shoot as often
      if (nftBonuses.detection_reduction) {
        enemies.forEach(enemy => {
          const dist = Math.hypot(enemy.x - player.x, enemy.y - player.y);
          // Reduce enemy aggro range
          if (dist > 400 * (1 - nftBonuses.detection_reduction / 100)) {
            enemy.shootCooldown = Math.max(enemy.shootCooldown, enemy.shootRate / 2);
          }
        });
      }

      // Update NFT ability states for HUD display
      const chainLightningInterval = nftBonuses.chain_lightning_interval || 0;
      setGameState(prev => ({
        ...prev,
        nftAbilities: {
          spawnShieldActive: player.hasShield && data.nftAbilityTimers.spawnShieldTimer > 0,
          spawnShieldRemaining: Math.ceil(data.nftAbilityTimers.spawnShieldTimer / 60),
          burnAuraActive: !!nftBonuses.burn_aura,
          burnAuraCooldown: 60 - data.nftAbilityTimers.burnAuraTick,
          slowAuraActive: !!nftBonuses.slow_aura,
          chainLightningReady: chainLightningInterval > 0 && data.nftAbilityTimers.chainLightningCounter >= chainLightningInterval - 1,
          chainLightningCounter: data.nftAbilityTimers.chainLightningCounter,
          chainLightningMax: chainLightningInterval,
          healthRegenCooldown: Math.ceil((300 - data.nftAbilityTimers.healthRegenTimer) / 60),
          healthRegenActive: !!nftBonuses.health_regen
        }
      }));
    }

    // Update bullets
    bullets.forEach((bullet, index) => {
      const shouldExplode = skipEntityTick ? false : bullet.update(canvas.height);
      
      if (shouldExplode && bullet.isGrenade) {
        // Grenade explosion damages nearby enemies (depth-aware)
        createExplosion(bullet.x, bullet.y, '#FF9500', 20);
        enemies.forEach((enemy) => {
          const dist = Math.hypot(enemy.x - bullet.x, enemy.y - bullet.y);
          const depthClose = enemy.isFlying() || Math.abs(bullet.groundY - enemy.groundY) < BULLET_DEPTH_THRESHOLD;
          if (dist < 100 && depthClose) {
            enemy.health -= bullet.damage * (1 - dist / 100);
            if (enemy.health <= 0 && !enemy.isDead) {
              enemy.isDead = true;
              createDeathAnimation(enemy, enemy.x + enemy.width / 2, enemy.y + enemy.height / 2);
              createExplosion(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2, '#FF9500', 15);
              triggerScreenShake(8, 15); // Big shake for explosions
              const points = enemy.getScoreValue();
              creditKill(points, bullet);
            }
          }
        });
      }

      if (bullet.x < data.cameraX - 50 || bullet.x > data.cameraX + canvas.width + 50) {
        bullet.active = false;
      }

      // Bullet collision with enemies (depth-aware)
      if (!bullet.isEnemy && !bullet.isGrenade) {
        enemies.forEach((enemy, enemyIndex) => {
          // Use depth-aware collision
          if (checkCollision2D(bullet, enemy, BULLET_DEPTH_THRESHOLD)) {
            // Rocket explosion with blast radius
            if (bullet.isRocket) {
              createExplosion(bullet.x, bullet.y, '#FF4444', 25);
              // Damage all enemies in blast radius (depth-aware)
              enemies.forEach((nearbyEnemy) => {
                const dist = Math.hypot(nearbyEnemy.x - bullet.x, nearbyEnemy.y - bullet.y);
                const depthClose = nearbyEnemy.isFlying() || Math.abs(bullet.groundY - nearbyEnemy.groundY) < BULLET_DEPTH_THRESHOLD;
                if (dist < bullet.blastRadius && depthClose) {
                  const blastDamage = bullet.damage * (1 - dist / bullet.blastRadius);
                  nearbyEnemy.health -= blastDamage;
                  
                  if (nearbyEnemy.health <= 0 && !nearbyEnemy.isDead) {
                    nearbyEnemy.isDead = true;
                    createDeathAnimation(nearbyEnemy, nearbyEnemy.x + nearbyEnemy.width / 2, nearbyEnemy.y + nearbyEnemy.height / 2);
                    createExplosion(nearbyEnemy.x + nearbyEnemy.width / 2, nearbyEnemy.y + nearbyEnemy.height / 2, '#FF9500', 15);
                    const points = nearbyEnemy.getScoreValue();
                    creditKill(points, bullet);
                  }
                }
              });
              bullet.active = false;
            } else if (bullet.isExplosive) {
              // Explosive rounds - explode on impact with blast damage (depth-aware)
              createExplosion(bullet.x, bullet.y, '#FF4500', 20);
              const blastRadius = 60;
              enemies.forEach((nearbyEnemy) => {
                const dist = Math.hypot(nearbyEnemy.x - bullet.x, nearbyEnemy.y - bullet.y);
                const depthClose = nearbyEnemy.isFlying() || Math.abs(bullet.groundY - nearbyEnemy.groundY) < BULLET_DEPTH_THRESHOLD;
                if (dist < blastRadius && depthClose) {
                  const blastDamage = bullet.getDamage() * (1 - dist / blastRadius);
                  nearbyEnemy.health -= blastDamage;
                  
                  if (nearbyEnemy.health <= 0 && !nearbyEnemy.isDead) {
                    nearbyEnemy.isDead = true;
                    createDeathAnimation(nearbyEnemy, nearbyEnemy.x + nearbyEnemy.width / 2, nearbyEnemy.y + nearbyEnemy.height / 2);
                    createExplosion(nearbyEnemy.x + nearbyEnemy.width / 2, nearbyEnemy.y + nearbyEnemy.height / 2, '#FF4500', 12);
                    const points = nearbyEnemy.getScoreValue();
                    creditKill(points, bullet);
                  }
                }
              });
              bullet.active = false;
            } else if (bullet.isLaser) {
              // Laser pierces through enemies (up to 3)
              const damage = bullet.getDamage();
              enemy.health -= damage;
              createExplosion(bullet.x, bullet.y, '#00FF00', 8);

              if (enemy.health <= 0 && !enemy.isDead) {
                enemy.isDead = true;
                createDeathAnimation(enemy, enemy.x + enemy.width / 2, enemy.y + enemy.height / 2);
                createExplosion(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2, '#00FF00', 15);
                
                const points = enemy.getScoreValue();
                creditKill(points, bullet);
              }
              
              // Laser can pierce up to 3 enemies
              bullet.pierceCount++;
              if (bullet.pierceCount >= 3) {
                bullet.active = false;
              }
            } else {
              // Regular bullet or tank shell
              let damage = bullet.getDamage();
              let isCritical = false;
              
              // Apply critical chance (Foot Stabber - Painful Surprise!)
              if (player.nftBonuses?.critical_chance && Math.random() < player.nftBonuses.critical_chance / 100) {
                damage *= 2.5; // Critical hits deal 2.5x damage
                isCritical = true;
                createExplosion(bullet.x, bullet.y, '#FFD700', 12); // Gold critical hit effect
              }
              
              // Apply NFT damage boost (Solar Knight)
              if (player.nftBonuses?.damage_boost) {
                damage *= (1 + player.nftBonuses.damage_boost / 100);
              }
              
              // Apply backstab multiplier (Shadow Assassin) - if hitting from behind
              if (player.nftBonuses?.backstab_multiplier && player.x > enemy.x) {
                damage *= player.nftBonuses.backstab_multiplier;
                createExplosion(bullet.x, bullet.y, '#1a1a2e', 10); // Shadow effect
              }
              
              enemy.health -= damage;
              
              // Chain Lightning (Thunder Lord) - every Nth shot chains
              if (player.nftBonuses?.chain_lightning_interval) {
                data.nftAbilityTimers.chainLightningCounter++;
                if (data.nftAbilityTimers.chainLightningCounter >= player.nftBonuses.chain_lightning_interval) {
                  data.nftAbilityTimers.chainLightningCounter = 0;
                  // Find nearby enemies to chain to
                  let chainCount = 0;
                  const maxChains = player.nftBonuses.chain_targets || 3;
                  enemies.forEach((chainEnemy) => {
                    if (chainEnemy !== enemy && chainCount < maxChains && !chainEnemy.isDead) {
                      const chainDist = Math.hypot(chainEnemy.x - enemy.x, chainEnemy.y - enemy.y);
                      if (chainDist < 200) { // Chain range
                        chainEnemy.health -= damage * 0.5; // 50% chain damage
                        createExplosion(chainEnemy.x + chainEnemy.width / 2, chainEnemy.y + chainEnemy.height / 2, '#FFFF00', 8);
                        chainCount++;
                        if (chainEnemy.health <= 0 && !chainEnemy.isBoss && !chainEnemy.isDead) {
                          chainEnemy.isDead = true;
                          createDeathAnimation(chainEnemy, chainEnemy.x + chainEnemy.width / 2, chainEnemy.y + chainEnemy.height / 2);
                          createExplosion(chainEnemy.x + chainEnemy.width / 2, chainEnemy.y + chainEnemy.height / 2, '#FFFF00', 15);
                          const chainPoints = chainEnemy.getScoreValue();
                          creditKill(chainPoints, bullet);
                        }
                      }
                    }
                  });
                }
              }
              
              // Blind chance (Solar Knight) - 10% chance to blind enemies
              if (player.nftBonuses?.blind_chance && Math.random() < player.nftBonuses.blind_chance / 100) {
                enemy.shootCooldown = enemy.shootRate * 2; // Blinded enemies can't shoot for longer
                createExplosion(enemy.x + enemy.width / 2, enemy.y + 10, '#FFD700', 6); // Blind flash
              }
              
              // Incendiary bullets (OFTS) - set enemies on fire
              if (player.nftBonuses?.incendiary_bullets && !enemy.isBoss) {
                enemy.onFire = true;
                enemy.fireTimer = (player.nftBonuses.fire_duration || 3) * 60; // Convert seconds to frames
                enemy.fireDamage = player.nftBonuses.fire_damage || 5;
                // Track shooter so fire-DOT kills attribute correctly in MP
                enemy.fireShooter = bullet.isRemote && bullet.shooter ? bullet.shooter : null;
                createExplosion(bullet.x, bullet.y, '#FF4500', 8); // Fire spark on impact
              }
              
              const explosionColor = bullet.doubleDamage ? '#FF1493' : 
                                     bullet.isSpread ? '#FF00FF' :
                                     bullet.isIncendiary ? '#FF4500' :
                                     bullet.isTankShell ? '#4A90D9' : '#5AC8FA';
              createExplosion(bullet.x, bullet.y, explosionColor, bullet.doubleDamage ? 8 : 5);
              createSparks(bullet.x, bullet.y, -bullet.direction, explosionColor, 6); // Sparks fly opposite direction

              if (enemy.health <= 0 && !enemy.isDead) {
                enemy.isDead = true;
                createDeathAnimation(enemy, enemy.x + enemy.width / 2, enemy.y + enemy.height / 2);
                createExplosion(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2, '#FF9500', 15);
                triggerScreenShake(enemy.isBoss ? 15 : 6, enemy.isBoss ? 30 : 12); // Bigger shake for boss
                
                let points = enemy.getScoreValue();
                
                // Apply style bonus (Foot Stabber) - score multiplier
                if (player.nftBonuses?.style_bonus) {
                  points = Math.floor(points * (1 + player.nftBonuses.style_bonus / 100));
                }
                
                // Add critical bonus points if it was a critical hit
                if (isCritical) {
                  points = Math.floor(points * 1.5); // 50% more points on critical kills
                }
                
                creditKill(points, bullet);
              }
              bullet.active = false;
            }
          }
        });
      }

      // Enemy bullets hitting player (depth-aware)
      if (bullet.isEnemy && checkCollision2D(bullet, player, BULLET_DEPTH_THRESHOLD)) {
        let incomingDamage = 10;
        // Apply fire resist (Molten Guardian) - reduces damage from explosions/bullets
        if (player.nftBonuses?.fire_resist) {
          incomingDamage *= (1 - player.nftBonuses.fire_resist / 100);
        }
        
        // Damage reflect (Foot Stabber) - reflect damage back to attacker
        if (player.nftBonuses?.damage_reflect && bullet.owner) {
          const reflectDamage = incomingDamage * (player.nftBonuses.damage_reflect / 100);
          bullet.owner.health -= reflectDamage;
          createExplosion(player.x + player.width / 2, player.y, '#808080', 8); // Gray reflect effect
          if (bullet.owner.health <= 0 && !bullet.owner.isDead) {
            bullet.owner.isDead = true;
            createDeathAnimation(bullet.owner, bullet.owner.x + bullet.owner.width / 2, bullet.owner.y + bullet.owner.height / 2);
            setGameState(prev => ({
              ...prev,
              score: prev.score + bullet.owner.getScoreValue(),
              kills: prev.kills + 1
            }));
          }
        }
        
        if (player.takeDamage(incomingDamage)) {
          createExplosion(bullet.x, bullet.y, '#FF3B30', 5);
          triggerScreenShake(4, 8); // Shake when hit
        }
        bullet.active = false;

        if (player.health <= 0) {
          // Read authoritative lives from the ref — see comment on data.lives
          // init. gameState.lives is stale within the game-loop closure.
          if ((data.lives || 0) > 0) {
            player.health = player.maxHealth;
            player.hasShield = true;
            player.shieldTimer = 180; // 3-second invulnerability after respawn
            data.currentKillStreak = 0; // streak resets on death
            data.comboCount = 0; // combo also resets on revival
            // Consume one life on the ref immediately; mirror to React state
            // so the HUD heart counter re-renders.
            data.lives = data.lives - 1;
            // Trigger revival animation: pink flash + slow-mo + heart particle burst
            data.revivalEffect = { active: true, timer: 90, duration: 90 }; // 1.5s @ 60fps
            // Burst of heart particles
            for (let i = 0; i < 18; i++) {
              const angle = (i / 18) * Math.PI * 2;
              const speed = 2 + Math.random() * 3;
              const heart = new Particle(
                player.x + player.width / 2,
                player.y + player.height / 2,
                '#FF1493'
              );
              heart.velocityX = Math.cos(angle) * speed;
              heart.velocityY = Math.sin(angle) * speed - 1;
              heart.size = 5 + Math.random() * 4;
              heart.life = 1;
              heart.decay = 0.012;
              heart.isHeart = true; // flagged so draw() can render a ♥
              data.particles.push(heart);
            }
            // Broadcast revival so peers can render the heart-particle flourish
            // above our character (P-2). Only the local player gets the slow-mo
            // — peers just see the visual celebration so they know what happened.
            const mpRev = multiplayerSyncRef.current;
            if (mpRev) {
              mpRev.sendGameAction('revival_triggered', {
                x: player.x + player.width / 2,
                y: player.y + player.height / 2,
              });
            }
            setGameState(prev => ({
              ...prev,
              lives: data.lives
            }));
            soundManager.playPowerUp();
            triggerScreenShake(8, 14);
          } else {
            data.gameRunning = false;
            data.currentKillStreak = 0;
            data.comboCount = 0;
            soundManager.playGameOver();
            soundManager.stopMusic();
            setGameState(prev => ({ ...prev, isGameOver: true, isPlaying: false }));
            onGameOver?.({
              score: gameState.score,
              level: gameState.level,
              kills: gameState.kills,
              max_kill_streak: data.maxKillStreak,
              bosses_defeated: data.bossesDefeated,
              kills_by_type: data.killsByType || {},
              defeated_boss_types: Object.keys(data.defeatedBossTypes || {}),
              time_played: Math.floor((Date.now() - (data.gameStartTime || Date.now())) / 1000)
            });
          }
        }
      }
    });

    data.bullets = bullets.filter(b => b.active);
    
    // Remove dead enemies after all bullet processing is complete
    // This avoids index shifting issues when splicing during forEach
    // CRITICAL: in-place mutation — see the comment at the earlier filter.
    // The destructured `enemies` at line 1127 MUST remain pointing at the
    // same array so the boss-defeated detection below sees the removal.
    // Also: BEFORE filtering, capture the name of any dead boss so the
    // "Boss Vanquished" stinger frame (rendered by GameCanvas during the
    // level transition) has the right label.
    {
      const _deadBoss = data.enemies.find(e => e.isDead && e.isBoss);
      if (_deadBoss) {
        data.lastBossName = _deadBoss.bossName || 'BOSS';
        data.lastBossReward = 500 + (gameState.level || 1) * 100; // matches kill points
        // Bestiary/BossRoster: record the boss type we just defeated (dedup).
        data.defeatedBossTypes = data.defeatedBossTypes || {};
        data.defeatedBossTypes[_deadBoss.type] = (data.defeatedBossTypes[_deadBoss.type] || 0) + 1;
      }
      // Bestiary: increment per-type kill counter for every dying enemy this
      // frame (both bosses and grunts). Cheap: at most a few dead per frame.
      data.killsByType = data.killsByType || {};
      for (const e of data.enemies) {
        if (e.isDead) {
          data.killsByType[e.type] = (data.killsByType[e.type] || 0) + 1;
        }
      }
      const _alive = data.enemies.filter(e => !e.isDead);
      data.enemies.length = 0;
      data.enemies.push(..._alive);
    }

    // Update power-ups (depth-aware)
    powerUps.forEach((powerUp, index) => {
      powerUp.update();
      
      // Calculate pickup range - increased by NFT bonus (Void Walker)
      let pickupRange = 300;
      if (player.nftBonuses?.power_up_magnet) {
        pickupRange *= (1 + player.nftBonuses.power_up_magnet / 100);
      }
      
      // Magnet effect - power-ups are attracted to player (or always if NFT has it)
      if (player.magnetActive || player.nftBonuses?.power_up_magnet) {
        const dx = player.x - powerUp.x;
        const dist = Math.abs(dx);
        
        if (dist < pickupRange) {
          const magnetForce = 5 * (1 - dist / pickupRange);
          powerUp.x += Math.sign(dx) * magnetForce;
          // Also attract depth
          const depthDiff = player.groundY - powerUp.groundY;
          powerUp.groundY += Math.sign(depthDiff) * magnetForce * 0.5;
        }
      }
      
      // Depth-aware pickup
      const depthClose = Math.abs(player.groundY - powerUp.groundY) < DEPTH_COLLISION_THRESHOLD * 1.5;
      if (checkCollision(player, { ...powerUp, y: player.y }) && depthClose) {
        applyPowerUp(powerUp);
        // Broadcast pickup so peers can remove this power-up from their list
        const mp = multiplayerSyncRef.current;
        if (mp) {
          mp.sendGameAction('powerup_collected', { id: powerUp.id });
        }
        powerUps.splice(index, 1);
      }
      
      if (powerUp.x < data.cameraX - 100) {
        powerUps.splice(index, 1);
      }
    });

    // Update particles
    particles.forEach((particle, index) => {
      particle.update();
      if (particle.life <= 0) {
        particles.splice(index, 1);
      }
    });

    // Update brass casings (Assault Rifle ejecta)
    if (data.brassCasings) {
      data.brassCasings.forEach((bc, idx) => {
        bc.update(canvas.height);
        if (bc.life <= 0) data.brassCasings.splice(idx, 1);
      });
    }

    // Update floating combat-text (P-2 "+50" rises and fades)
    if (data.floatingTexts) {
      for (let i = data.floatingTexts.length - 1; i >= 0; i--) {
        data.floatingTexts[i].update();
        if (data.floatingTexts[i].life <= 0) data.floatingTexts.splice(i, 1);
      }
    }

    // Combo decay (P-1): if no kill within COMBO_WINDOW_MS, drop combo to 0.
    // Also expose live tier so HUD/UI can read it.
    if (data.comboCount > 0 && Date.now() - data.lastKillTime > COMBO_WINDOW_MS) {
      data.comboCount = 0;
    }
    // Mirror to React state at most every 4 frames (reduces re-renders)
    data._comboMirrorTick = (data._comboMirrorTick || 0) + 1;
    if (data._comboMirrorTick > 4) {
      data._comboMirrorTick = 0;
      if (gameState.combo !== data.comboCount) {
        setGameState(prev => ({ ...prev, combo: data.comboCount }));
      }
    }

    // Level progression with boss fight (host or single-player only)
    if (!data.bossSpawned && !gameState.showLevelTransition && !isGuest) {
      data.levelProgress += 0.02;
      
      // Spawn boss at 90% level progress
      if (data.levelProgress >= 90 && !gameState.bossActive) {
        spawnBoss(canvas.width, data.level || gameState.level);
      }
    } else if (isGuest && !data.bossSpawned && !gameState.showLevelTransition) {
      // Guest: still advance the local progress bar so the UI feels live
      data.levelProgress = Math.min(90, data.levelProgress + 0.02);
    }

    // === Boss-defeated detection (P-1 robustness) =====================
    // Dedup guards use `data.*` refs (mutated synchronously) instead of
    // gameState reads (set via setState — closure-stale between frames).
    // This eliminates the entire class of "boss died but level didn't
    // advance" bugs caused by React batching, StrictMode double-renders,
    // or two ticks firing the trigger before showLevelTransition commits.
    const bossEnemy = enemies.find(e => e.isBoss);
    const bossWasHere = !!data.bossSpawned;
    const noTransitionActive = !data.transitionPhase;
    if (bossWasHere && !bossEnemy && noTransitionActive) {
      // Boss defeated — start level transition: stinger (0.6s) → fadeOut
      // (0.3s) → showStats (1.0s) → fadeIn (0.7s). Total 2.6s.
      console.info('[transition] boss defeated → starting level transition', {
        level: gameState.level,
        bossName: data.lastBossName,
        reward: data.lastBossReward,
      });
      data.bossSpawned = false;
      data.bossesDefeated = (data.bossesDefeated || 0) + 1;
      data.levelTransitionStart = Date.now();
      data.transitionPhase = 'stinger';
      setGameState(prev => ({
        ...prev,
        bossActive: false,
        bossDefeated: true,
        showLevelTransition: true,
        levelTransitionTimer: 2.6,
        transitionPhase: 'stinger',
        defeatedBossName: data.lastBossName || 'BOSS',
        defeatedBossReward: data.lastBossReward || 500,
      }));
    }

    // Handle level transition (2.6s cut screen with boss-vanquished stinger).
    // Guard on data.transitionPhase (sync) — not gameState (async). This makes
    // the cleanup branch the single source of truth for advancing the level.
    if (data.transitionPhase) {
      const startedAt = data.levelTransitionStart || Date.now();
      const elapsedMs = Date.now() - startedAt;
      const elapsed = elapsedMs / 1000;
      const totalDuration = 2.6; // 0.6 stinger + 0.3 fadeOut + 1.0 stats + 0.7 fadeIn
      const remaining = Math.max(0, totalDuration - elapsed);

      // Watchdog: if we've been transitioning for longer than 5s (almost 2x
      // the 2.6s budget) something stalled the per-frame state advancement
      // (tab throttled, devtools paused, etc.). Force completion so level
      // progression never permanently gets stuck.
      const watchdogTripped = elapsedMs > 5000;
      if (watchdogTripped) {
        console.warn('[transition] watchdog firing — forcing level advance after', elapsedMs, 'ms');
        // Fire-and-forget server signal so we can conclusively prove the
        // transition pipeline is healthy in the wild (or catch a regression
        // the moment it ships). Never blocks gameplay — sendBeacon or
        // fetch(keepalive) under the hood.
        try {
          const _mp = multiplayerSyncRef.current;
          reportTransitionWatchdog({
            level: gameState.level,
            elapsedMs,
            walletAddress: (_mp && _mp.walletAddress) || 'guest',
            multiplayer: !!(_mp && _mp.isMultiplayer),
            isHost: !!(_mp && _mp.isHost),
          });
        } catch (_) { /* never break gameplay on a telemetry throw */ }
      }

      let phase = 'stinger';
      if (elapsed < 0.6) {
        phase = 'stinger';     // BOSS VANQUISHED banner pop-in
      } else if (elapsed < 0.9) {
        phase = 'fadeOut';     // Quick fade to black
      } else if (elapsed < 1.9) {
        phase = 'showStats';   // Show level-up stats
      } else {
        phase = 'fadeIn';      // Fade back to game
      }

      if (remaining <= 0 || watchdogTripped) {
        // Transition complete - advance to next level.
        // CRITICAL: source the current level from `data.level` (ref), not
        // `gameState.level` (stale closure). Otherwise a same-frame double
        // read after the previous transition could compute the wrong next.
        const nextLevel = (data.level || gameState.level || 1) + 1;
        console.info('[transition] complete → advancing to level', nextLevel);
        data.levelProgress = 0;
        data.bossSpawned = false;
        data.transitionPhase = null;
        data.levelTransitionStart = null;
        data.lastBossName = null;
        data.lastBossReward = null;
        // Update the authoritative level ref BEFORE setGameState so the game
        // loop's drawBackground/spawnEnemy/spawnBoss reads reflect the new
        // level on the very next frame — even though React state is async.
        data.level = nextLevel;
        soundManager.playLevelUp();
        soundManager.startMusic(nextLevel);
        setGameState(prev => ({
          ...prev,
          level: nextLevel,
          showLevelTransition: false,
          bossDefeated: false,
          bossActive: false,
          levelTransitionTimer: 0,
          transitionPhase: null,
          defeatedBossName: null,
          defeatedBossReward: null,
        }));
      } else {
        setGameState(prev => ({
          ...prev,
          levelTransitionTimer: remaining.toFixed(1),
          transitionPhase: phase
        }));
      }
    }

    // Update environment particles
    updateEnvironmentParticles(canvas, data.cameraX);

    // Update death animations
    updateDeathAnimations(canvas.height);

    // Apply screen shake
    ctx.save();
    if (data.screenShake.duration > 0) {
      const shakeX = (Math.random() - 0.5) * data.screenShake.intensity * 2;
      const shakeY = (Math.random() - 0.5) * data.screenShake.intensity * 2;
      ctx.translate(shakeX, shakeY);
      data.screenShake.duration--;
      data.screenShake.intensity *= 0.9; // Decay intensity
    }

    // Draw everything - sorted by depth for 2.5D rendering
    drawEnvironmentParticles(ctx, data.cameraX);
    particles.forEach(p => p.draw(ctx, data.cameraX));
    if (data.brassCasings) data.brassCasings.forEach(bc => bc.draw(ctx, data.cameraX));
    powerUps.forEach(p => p.draw(ctx, data.cameraX, canvas.height));
    drawDeathAnimations(ctx, data.cameraX, canvas.height);
    // Collect all ground entities and sort by groundY (back-to-front)
    const renderables = [];
    enemies.forEach(e => renderables.push({ groundY: e.groundY || 0, draw: () => e.draw(ctx, data.cameraX, canvas.height) }));
    renderables.push({ groundY: player.groundY, draw: () => {
      // Combo aura (P-1): pulsing colored ring/glow behind the player when combo >= 3
      const tier = getComboTier(data.comboCount);
      if (tier.glow > 0) {
        const px = player.x + player.width / 2 - data.cameraX;
        const py = player.y + player.height / 2;
        const pulse = 0.7 + Math.sin(Date.now() / 110) * 0.3;
        ctx.save();
        ctx.globalAlpha = tier.glow * pulse * 0.55;
        ctx.shadowColor = tier.color;
        ctx.shadowBlur = 28;
        ctx.fillStyle = tier.color;
        ctx.beginPath();
        ctx.ellipse(px, py + 12, 36, 14, 0, 0, Math.PI * 2);
        ctx.fill();
        // Outer ring at character height
        ctx.globalAlpha = tier.glow * pulse * 0.85;
        ctx.shadowBlur = 18;
        ctx.lineWidth = 3;
        ctx.strokeStyle = tier.color;
        ctx.beginPath();
        ctx.ellipse(px, py, 32, 42, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      player.draw(ctx, data.cameraX);
    }});
    // Add other multiplayer players (P-1 lerped: smooth tween toward latest snapshot)
    const mpSync = multiplayerSyncRef.current;
    if (mpSync) {
      const otherPlayers = mpSync.otherPlayers || {};
      // Maintain a render-side mirror of peer states that lerps toward the
      // authoritative `otherPlayers` snapshot. Peers update at ~10 Hz so direct
      // rendering snaps; lerping at 0.28/frame gives ~85% convergence per snapshot
      // interval @ 60 fps and feels like locally-driven movement.
      if (!data._peerRenderState) data._peerRenderState = new Map();
      const seen = new Set();
      const PEER_LERP = 0.28;
      const TRAIL_INTERVAL = 50; // ms between trail samples
      const TRAIL_MAX = 14;      // ghost frames per peer
      const nowMs = Date.now();
      Object.entries(otherPlayers).forEach(([wallet, pState]) => {
        seen.add(wallet);
        let render = data._peerRenderState.get(wallet);
        if (!render) {
          // First time seeing this peer — snap to authoritative position
          render = {
            x: pState.x ?? 0,
            y: pState.y ?? 0,
            groundY: pState.groundY ?? DEFAULT_GROUND_Y,
            trail: [],
            lastTrailAt: 0,
          };
          data._peerRenderState.set(wallet, render);
        } else {
          render.x += ((pState.x ?? render.x) - render.x) * PEER_LERP;
          render.y += ((pState.y ?? render.y) - render.y) * PEER_LERP;
          render.groundY += ((pState.groundY ?? render.groundY) - render.groundY) * PEER_LERP;
        }
        // Sample a trail point every TRAIL_INTERVAL ms (only if peer is moving)
        if (nowMs - render.lastTrailAt > TRAIL_INTERVAL) {
          const last = render.trail[render.trail.length - 1];
          const moved = !last || Math.hypot(render.x - last.x, render.y - last.y) > 1.5;
          if (moved) {
            render.trail.push({
              x: render.x,
              y: render.y,
              groundY: render.groundY,
              direction: pState.direction || 1,
              t: nowMs,
            });
            if (render.trail.length > TRAIL_MAX) render.trail.shift();
            render.lastTrailAt = nowMs;
          }
        }
        // Compose a draw-state that uses the lerped position but keeps all
        // other authoritative fields (health, direction, weapon, color, nftId)
        const drawState = {
          ...pState,
          x: render.x,
          y: render.y,
          groundY: render.groundY,
        };
        // Cache trail color on the render-state for the trail draw pass
        render.color = pState.color || '#9945FF';
        renderables.push({
          groundY: drawState.groundY,
          draw: () => {
            drawOtherPlayer(ctx, drawState, data.cameraX, canvas.height);
            // Wallet name tag above peer (P-2): truncated wallet in trail color,
            // fades with horizontal distance from local player camera.
            const nameX = drawState.x + 25 - data.cameraX;
            const nameY = drawState.y - 14;
            // Distance fade: full alpha within 250 px of local player, fades to 0.35 by 700+
            const dx = Math.abs((drawState.x + 25) - (player.x + player.width / 2));
            const tagAlpha = Math.max(0.35, 1 - Math.max(0, dx - 250) / 450);
            const short = wallet.length > 10
              ? `${wallet.slice(0, 4)}…${wallet.slice(-4)}`
              : wallet;
            ctx.save();
            ctx.globalAlpha = tagAlpha;
            ctx.font = 'bold 10px "Press Start 2P", monospace';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            // Black outline for legibility on busy backgrounds
            ctx.lineWidth = 3;
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
            ctx.strokeText(short, nameX, nameY);
            // Colored fill in peer's trail color
            ctx.shadowColor = render.color;
            ctx.shadowBlur = 4;
            ctx.fillStyle = render.color;
            ctx.fillText(short, nameX, nameY);
            ctx.restore();
          }
        });
      });
      // Drop render state for peers that have disconnected
      for (const wallet of data._peerRenderState.keys()) {
        if (!seen.has(wallet)) data._peerRenderState.delete(wallet);
      }

      // === PEER TRAIL EFFECT (P-1) ===
      // Draw faint colored ghost-silhouettes for each peer's recent positions.
      // Drawn BEFORE renderables.sort/forEach so trails sit behind peer sprites.
      // Older trail points fade more (alpha = i/length * 0.35) and shrink slightly.
      data._peerRenderState.forEach((render) => {
        if (!render.trail || render.trail.length === 0) return;
        const len = render.trail.length;
        ctx.save();
        for (let i = 0; i < len; i++) {
          const ghost = render.trail[i];
          const ageRatio = (i + 1) / len; // 1 = newest, ~0 = oldest
          const alpha = ageRatio * 0.32;
          const screenX = ghost.x - data.cameraX;
          if (screenX < -60 || screenX > canvas.width + 60) continue;
          const w = 38 * (0.55 + ageRatio * 0.45);
          const h = 58 * (0.55 + ageRatio * 0.45);
          const cx = screenX + 25 - w / 2;
          const cy = ghost.y + 10 - h / 2;
          // Solid color silhouette with low alpha — simple readable ghost
          ctx.globalAlpha = alpha;
          ctx.fillStyle = render.color;
          ctx.shadowColor = render.color;
          ctx.shadowBlur = 8 * ageRatio;
          ctx.fillRect(cx + (w * 0.18), cy, w * 0.64, h * 0.95);
          // Glowing colored outline
          ctx.shadowBlur = 0;
          ctx.globalAlpha = alpha * 0.8;
          ctx.strokeStyle = render.color;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(cx + (w * 0.18), cy, w * 0.64, h * 0.95);
        }
        ctx.restore();
      });
      // === END PEER TRAIL EFFECT ===
    }
    renderables.sort((a, b) => a.groundY - b.groundY);
    renderables.forEach(r => r.draw());
    
    bullets.forEach(b => b.draw(ctx, data.cameraX));
    drawMuzzleFlash(ctx, data.cameraX);

    // Draw floating combat-text on top of everything (P-2)
    if (data.floatingTexts) data.floatingTexts.forEach(ft => ft.draw(ctx, data.cameraX));
    
    // === MULTIPLAYER SYNC ===
    const mpSyncForState = multiplayerSyncRef.current;
    if (mpSyncForState) {
      const now = Date.now();
      
      // Send player state at sync interval (with groundY)
      if (now - lastSyncTime.current > SYNC_INTERVAL) {
        mpSyncForState.sendPlayerState({
          x: player.x,
          y: player.y,
          groundY: player.groundY,
          health: player.health,
          direction: player.direction,
          isJumping: player.isJumping,
          currentWeapon: player.currentWeapon,
          color: playerColor,
          nftId: nftId,
          score: gameState.score,
          kills: gameState.kills
        });
        lastSyncTime.current = now;
      }
      
      // Add synced bullets from other players (peer's tracers visible AND damage local enemies)
      if (mpSyncForState.syncedBullets && mpSyncForState.syncedBullets.length > 0) {
        mpSyncForState.syncedBullets.forEach(b => {
          if (b.rendered) return;
          // Map peer-broadcast `kind` to the right Bullet flags so rockets/tank/rifle render with their own style
          const isGrenade = b.kind === 'grenade';
          const isRocket = b.kind === 'rocket';
          const isTank = b.kind === 'tank';
          const isRifle = b.kind === 'rifle';
          const isRapid = !!b.rapidFire && !isRocket && !isTank && !isRifle;
          const newBullet = new Bullet(b.x, b.y, b.direction, false, isGrenade, isRocket, isTank, isRapid, isRifle);
          newBullet.groundY = b.groundY || DEFAULT_GROUND_Y;
          newBullet.isLaser = !!b.isLaser;
          newBullet.isExplosive = !!b.isExplosive;
          newBullet.doubleDamage = !!b.doubleDamage;
          newBullet.isRemote = true; // for collision attribution; not re-broadcast
          newBullet.shooter = b.owner; // wallet of original shooter (for kill attribution)
          bullets.push(newBullet);
          b.rendered = true;
        });
      }

      // Drain pending kill credits (kills attributed to me by host of remote bullets)
      if (mpSyncForState.consumeKillCredits) {
        const credits = mpSyncForState.consumeKillCredits();
        if (credits.length > 0) {
          let totalPoints = 0;
          for (const c of credits) totalPoints += (c.points || 0);
          setGameState(prev => ({
            ...prev,
            score: prev.score + totalPoints,
            kills: prev.kills + credits.length
          }));
          onScoreUpdate?.(totalPoints);
          // Float "+50" above my own player for each credited kill
          for (const c of credits) {
            data.floatingTexts.push(new FloatingText(
              player.x + player.width / 2,
              player.y - 10,
              `+${c.points || 0}`,
              '#FFD700'
            ));
          }
        }
      }

      // Drain power-up pickups from peers and remove them from local list
      if (mpSyncForState.consumePowerupPickups) {
        const ids = mpSyncForState.consumePowerupPickups();
        if (ids.length > 0) {
          const idSet = new Set(ids);
          // Mutate in place to keep destructured `powerUps` ref valid for next frame
          const filtered = data.powerUps.filter(p => !idSet.has(p.id));
          data.powerUps.length = 0;
          data.powerUps.push(...filtered);
        }
      }

      // Drain peer revival events — spawn heart flourish above their character
      // (P-2 sync revival animation across peers).
      if (mpSyncForState.consumeRevivals) {
        const revivals = mpSyncForState.consumeRevivals();
        for (const rev of revivals) {
          for (let i = 0; i < 14; i++) {
            const angle = (i / 14) * Math.PI * 2;
            const speed = 1.8 + Math.random() * 2.5;
            const heart = new Particle(rev.x, rev.y, '#FF1493');
            heart.velocityX = Math.cos(angle) * speed;
            heart.velocityY = Math.sin(angle) * speed - 1;
            heart.size = 4 + Math.random() * 4;
            heart.life = 1;
            heart.decay = 0.014;
            heart.isHeart = true;
            data.particles.push(heart);
          }
          // Floating "REVIVED!" text in pink so peers know what happened
          data.floatingTexts.push(new FloatingText(rev.x, rev.y - 20, 'REVIVED!', '#FF1493'));
        }
      }

      // Host broadcasts authoritative enemy snapshot + game-level mirror + power-ups (~10Hz)
      if (mpSyncForState.isHost) {
        if (now - (data._lastEnemySync || 0) > 100) {
          mpSyncForState.sendEnemySync(data.enemies, {
            bossActive: !!gameState.bossActive,
            bossDefeated: !!gameState.bossDefeated,
            bossSpawned: !!data.bossSpawned,
            levelProgress: data.levelProgress,
            level: gameState.level
          }, data.powerUps);
          data._lastEnemySync = now;
        }
      }
    }
    // === END MULTIPLAYER SYNC ===

    ctx.restore(); // Restore from screen shake

    // Revival animation overlay: pink flash that pulses then fades over 1.5s
    if (data.revivalEffect.active) {
      const t = data.revivalEffect.timer;
      const dur = data.revivalEffect.duration;
      if (t > 0) {
        // Strong flash for first 20 frames, then quick fade
        const phase = (dur - t) / dur; // 0 → 1 over the animation
        let alpha;
        if (phase < 0.15) alpha = 0.55 * (1 - phase / 0.15) + 0.15; // bright initial flash
        else alpha = 0.18 * (1 - phase); // residual pink wash
        ctx.fillStyle = `rgba(255, 20, 147, ${alpha})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // "REVIVED!" text in the first half
        if (phase < 0.55) {
          const textAlpha = Math.min(1, (1 - phase / 0.55));
          ctx.save();
          ctx.fillStyle = `rgba(255, 255, 255, ${textAlpha})`;
          ctx.shadowColor = '#FF1493';
          ctx.shadowBlur = 20;
          ctx.font = 'bold 32px "Press Start 2P", monospace';
          ctx.textAlign = 'center';
          ctx.fillText('REVIVED!', canvas.width / 2, canvas.height / 2 - 40);
          ctx.restore();
        }
        data.revivalEffect.timer = t - 1;
        if (data.revivalEffect.timer <= 0) data.revivalEffect.active = false;
      }
    }

    // Level progress bar (show boss indicator at end)
    ctx.fillStyle = '#333';
    ctx.fillRect(canvas.width / 2 - 100, 20, 200, 10);
    ctx.fillStyle = data.levelProgress >= 90 ? '#FF3B30' : '#9945FF';
    ctx.fillRect(canvas.width / 2 - 100, 20, Math.min(data.levelProgress, 100) * 2, 10);
    
    // Boss indicator
    if (data.levelProgress >= 85 && !gameState.bossActive) {
      ctx.fillStyle = '#FF3B30';
      ctx.font = 'bold 10px "Press Start 2P", monospace';
      ctx.textAlign = 'center';
      ctx.fillText('BOSS INCOMING!', canvas.width / 2, 45);
    }

    animationFrameRef.current = requestAnimationFrame(gameLoop);
  }, [canvasRef, gameState, onGameOver, onScoreUpdate, spawnEnemy, spawnPowerUp, applyPowerUp, spawnBoss]);

  const startGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Cancel any existing animation frame first
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Get the actual displayed dimensions of the canvas
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Initialize sound on first user interaction
    soundManager.init();
    soundManager.startMusic(1);

    const data = gameDataRef.current;
    
    // Reset all game data
    data.player = new Player(100, 0, playerColor, playerSpriteUrl, nftId);
    data.enemies = [];
    data.bullets = [];
    data.particles = [];
    data.brassCasings = [];
    data.floatingTexts = [];
    data._peerRenderState = new Map();
    // Combo meter state (P-1)
    data.comboCount = 0;
    data.lastKillTime = 0;
    data.comboPeak = 0;
    data.revivalEffect = { active: false, timer: 0, duration: 0 };
    data.powerUps = [];
    data.cameraX = 0;
    data.spawnTimer = 0;
    data.powerUpTimer = 0;
    data.levelProgress = 0;
    data.bossSpawned = false;
    data.levelTransitionStart = 0;
    data.lastShot = 0;
    data.lastWeaponSwitch = 0;
    data.gameRunning = true;
    data.gameStartTime = Date.now();
    data.tournamentTimer = tournamentTimeLimit > 0 ? tournamentTimeLimit : 0;
    data.tournamentLastTick = 0;
    // Reset kill streak / boss / life-drop counters
    data.currentKillStreak = 0;
    data.maxKillStreak = 0;
    data.bossesDefeated = 0;
    // Bestiary/BossRoster: per-game counters, forwarded in onGameOver payload
    data.killsByType = {};
    data.defeatedBossTypes = {};
    data.lastKillCount = 0;
    data.killsForLifeDrop = 0;
    data.nextLifeDropAt = 15 + Math.floor(Math.random() * 6); // 15-20
    // Extra Life counter — authoritative source of truth during gameplay
    data.lives = 0;
    // Level ref — must reset to 1 so a new game starts with the correct
    // sky/ground/enemy palette. See gameDataRef.level init comment.
    data.level = 1;

    // TEST HOOK (dev/preview only): expose the game data ref and applyPowerUp
    // so Playwright can deterministically simulate rare events like the ♥
    // Extra Life pickup. Stripped in production builds via NODE_ENV check.
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      window.__gameEngineTestHook = {
        gameDataRef,
        applyPowerUp,
        POWER_UP_TYPES,
      };
    }

    // Apply owned weapons (one-time purchases — load loadout at game start)
    if (ownedWeapons.includes('rocket_launcher')) {
      data.player.rocketAmmo = 10;
    }
    // 'machine_gun' is the renamed mini_tank — same gameplay slot (high-damage twin-cannon)
    if (ownedWeapons.includes('machine_gun') || ownedWeapons.includes('mini_tank')) {
      data.player.tankAmmo = 30;
    }
    // 'assault_rifle' grants 100 rounds of high-velocity neon-green ammo at game start
    if (ownedWeapons.includes('assault_rifle')) {
      data.player.rifleAmmo = 100;
    }
    // 'grenade' weapon grants 10 grenades at game start
    if (ownedWeapons.includes('grenade')) {
      data.player.grenades = (data.player.grenades || 0) + 10;
    }

    // Apply NFT character bonuses
    const bonuses = nftBonusesRef.current;
    if (bonuses) {
      // Spawn shield - invincibility at start
      if (bonuses.spawn_shield) {
        data.player.hasShield = true;
        data.player.shieldTimer = bonuses.spawn_shield * 60; // Convert seconds to frames
        data.nftAbilityTimers.spawnShieldTimer = bonuses.spawn_shield * 60;
      }
      // Speed boost - permanent
      if (bonuses.speed_boost) {
        data.player.nftSpeedBoost = bonuses.speed_boost / 100; // Convert percentage
      }
      // Damage boost - permanent
      if (bonuses.damage_boost) {
        data.player.nftDamageBoost = bonuses.damage_boost / 100;
      }
      // Store all bonuses on player for easy access
      data.player.nftBonuses = bonuses;
      // Reset ability timers
      data.nftAbilityTimers.healthRegenTimer = 0;
      data.nftAbilityTimers.chainLightningCounter = 0;
      data.nftAbilityTimers.burnAuraTick = 0;
    }

    setGameState({
      score: 0,
      level: 1,
      kills: 0,
      lives: 0,
      isPlaying: true,
      isPaused: false,
      isGameOver: false,
      grenades: data.player.grenades || 0,
      bossActive: false,
      bossDefeated: false,
      showLevelTransition: false,
      levelTransitionTimer: 0,
      transitionPhase: null,
      rocketAmmo: data.player.rocketAmmo,
      tankAmmo: data.player.tankAmmo,
      rifleAmmo: data.player.rifleAmmo,
      currentWeapon: 'default',
      tournamentTimeRemaining: tournamentTimeLimit > 0 ? tournamentTimeLimit : null
    });

    // Start new game loop
    animationFrameRef.current = requestAnimationFrame(gameLoop);
  }, [canvasRef, playerColor, gameLoop, ownedWeapons, playerSpriteUrl]);

  const pauseGame = useCallback(() => {
    const data = gameDataRef.current;
    setGameState(prev => {
      if (!prev.isPaused) {
        // Pausing the game
        data.gameRunning = false;
        soundManager.stopMusic();
      } else {
        // Resuming the game
        data.gameRunning = true;
        soundManager.startMusic(gameDataRef.current.level || 1);
        // Resume the game loop - always schedule a new frame
        animationFrameRef.current = requestAnimationFrame(gameLoop);
      }
      return { ...prev, isPaused: !prev.isPaused };
    });
  }, [gameLoop]);

  const stopGame = useCallback(() => {
    const data = gameDataRef.current;
    data.gameRunning = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    soundManager.stopMusic();
    setGameState(prev => ({ ...prev, isPlaying: false }));
  }, []);

  // Keyboard controls - 2.5D: WASD/Arrows for 4-directional movement, Shift/Z for jump
  useEffect(() => {
    const handleKeyDown = (e) => {
      const data = gameDataRef.current;
      switch (e.key.toLowerCase()) {
        case 'arrowleft':
        case 'a':
          data.keys.left = true;
          break;
        case 'arrowright':
        case 'd':
          data.keys.right = true;
          break;
        case 'arrowup':
        case 'w':
          data.keys.up = true; // Now = depth movement (walk back)
          break;
        case 'arrowdown':
        case 's':
          data.keys.down = true; // Depth movement (walk forward)
          break;
        case 'shift':
        case 'z':
          data.keys.jump = true; // Jump (separate from depth)
          break;
        case ' ':
          data.keys.shoot = true;
          e.preventDefault();
          break;
        case 'g':
        case 'e':
          data.keys.grenade = true;
          break;
        case 'q':
          data.keys.switchWeapon = true;
          break;
        case 'p':
        case 'escape':
          pauseGame();
          break;
        default:
          break;
      }
    };

    const handleKeyUp = (e) => {
      const data = gameDataRef.current;
      switch (e.key.toLowerCase()) {
        case 'arrowleft':
        case 'a':
          data.keys.left = false;
          break;
        case 'arrowright':
        case 'd':
          data.keys.right = false;
          break;
        case 'arrowup':
        case 'w':
          data.keys.up = false;
          break;
        case 'arrowdown':
        case 's':
          data.keys.down = false;
          break;
        case 'shift':
        case 'z':
          data.keys.jump = false;
          break;
        case ' ':
          data.keys.shoot = false;
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pauseGame]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      soundManager.stopMusic();
    };
  }, []);

  // Function to set keys from touch controls
  const setKeys = useCallback((updater) => {
    const data = gameDataRef.current;
    if (typeof updater === 'function') {
      const newKeys = updater(data.keys);
      Object.assign(data.keys, newKeys);
    } else {
      Object.assign(data.keys, updater);
    }
  }, []);

  return {
    gameState,
    startGame,
    pauseGame,
    stopGame,
    setKeys
  };
};

export default useGameEngine;
